<template>
  <div style="margin:20px">

    <FiPageRail title="Account & Billing Stats" />

    <div class="tenant-toolbar">
      <template v-if="!loading">
      <div class="tenant-filters-row">
        <span @click="filter_by_status(null,null)" class="tenant-filter-chip tenant-filter-clickable">{{filtered_tenant_count}}/{{items_full.length}}</span>
        <span
          v-if="show_filter_chip(billing_group_cnt, selected_billing_groups_only)"
          class="tenant-chip tenant-filter-clickable tenant-chip-billing-group"
          :class="{ 'tenant-chip-selected': selected_billing_groups_only }"
          @click="filter_by_billing_groups_only()"
        >Groups: {{ billing_group_cnt }}</span>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Feature Filters</span>
        <template v-for="(label, key) in feature_filters">
          <span
            v-if="show_filter_chip(feature_counts[key], selected_feature === key)"
            :key="key"
            :class="feature_filter_chip_classes(key)"
            :title="feature_filter_button_title(key)"
            @click="filter_by_status('feature', key)"
          >{{ label }}: {{ feature_counts[key] || 0 }}{{ billing_loaded ? feature_stripe_total_labels[key] : '' }}</span>
        </template>
      </div>
      <div class="tenant-counts-filters-row">
        <span class="tenant-filter-label">Counts</span>
        <span class="tenant-filter-chip">Vehicles: {{vehicle_cnt}}</span>
        <span class="tenant-filter-chip">Drivers: {{driver_cnt}}</span>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Vehicles</span>
        <template v-for="tier in vehicle_tier_filters">
          <span
            v-if="show_filter_chip(vehicle_tier_counts[tier.key], selected_vehicle_tier === tier.key)"
            :key="'vehicle-tier-' + tier.key"
            @click="filter_by_status('vehicleTier', tier.key)"
            :class="vehicle_tier_badge_classes(tier.key)"
          >{{ tier.label }}: {{ vehicle_tier_counts[tier.key] || 0 }}</span>
        </template>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Orgs</span>
        <template v-for="org in motorcoach_organization_filters">
          <span
            v-if="show_filter_chip(motorcoach_org_counts[org], selected_motorcoach_org === org)"
            :key="'motorcoach-org-filter-' + org"
            @click="filter_by_status('motorcoachOrg', org)"
            :class="motorcoach_org_badge_classes(org)"
          >{{ org }}: {{ motorcoach_org_counts[org] || 0 }}</span>
        </template>
      </div>
      <div class="tenant-feature-filters-row">
        <span class="tenant-filter-label">Ops</span>
        <span v-if="show_filter_chip(cm_cnt, selected_tenant_type === 'CM')" @click="filter_by_status('tenant', 'CM')" :class="tenant_type_badge_classes('CM')">CM: {{cm_cnt}}</span>
        <span v-if="show_filter_chip(tbn_cnt, selected_tenant_type === 'TBN')" @click="filter_by_status('tenant', 'TBN')" :class="tenant_type_badge_classes('TBN')">TBN: {{tbn_cnt}}</span>
        <span v-if="show_filter_chip(fasttrak_cnt, selected_tenant_type === 'FASTTRAK')" @click="filter_by_status('tenant', 'FASTTRAK')" :class="tenant_type_badge_classes('FASTTRAK')">FASTTRAK: {{fasttrak_cnt}}</span>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">GPS</span>
        <span v-if="show_filter_chip(samsara_cnt, selected_telematics_type === 'SAMSARA')" @click="filter_by_status('telematics', 'SAMSARA')" :class="telematics_type_badge_classes('SAMSARA')">SAMSARA: {{samsara_cnt}}</span>
        <span v-if="show_filter_chip(saucon_cnt, selected_telematics_type === 'SAUCON')" @click="filter_by_status('telematics', 'SAUCON')" :class="telematics_type_badge_classes('SAUCON')">SAUCON: {{saucon_cnt}}</span>
        <span v-if="show_filter_chip(zonar_cnt, selected_telematics_type === 'ZONAR')" @click="filter_by_status('telematics', 'ZONAR')" :class="telematics_type_badge_classes('ZONAR')">ZONAR: {{zonar_cnt}}</span>
        <span v-if="show_filter_chip(motive_cnt, selected_telematics_type === 'MOTIVE')" @click="filter_by_status('telematics', 'MOTIVE')" :class="telematics_type_badge_classes('MOTIVE')">MOTIVE: {{motive_cnt}}</span>
        <span v-if="show_filter_chip(none_cnt, selected_telematics_type === 'NONE')" @click="filter_by_status('telematics', 'NONE')" :class="telematics_type_badge_classes('NONE')">NONE: {{none_cnt}}</span>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Maint</span>
        <template v-for="(label, key) in maint_filters">
          <span
            v-if="show_filter_chip(maint_counts[key], selected_maint === key)"
            :key="'maint-filter-' + key"
            @click="filter_by_status('maint', key)"
            :class="maint_badge_classes(key)"
          >{{ label }}: {{ maint_counts[key] || 0 }}</span>
        </template>
      </div>
      <div v-if="billing_loaded" class="tenant-feature-filters-row">
        <span class="tenant-filter-label">Billing</span>
        <span class="tenant-filter-chip">Est: {{ pricing_totals.monthly }}</span>
        <span class="tenant-filter-chip">Stripe: {{ pricing_totals.stripe }}</span>
        <span
          class="tenant-filter-chip tenant-filter-clickable"
          :class="{ 'tenant-chip-selected': selected_pricing_gap === 'gap_positive' }"
          @click="filter_by_pricing_gap('gap_positive')"
        >Gap: {{ pricing_totals.gap }}</span>
        <span class="tenant-filter-sep">| Gaps:</span>
        <template v-for="range in pricing_gap_range_filters">
          <span
            v-if="show_filter_chip(pricing_gap_counts[range.key], selected_pricing_gap === range.key)"
            :key="'gap-' + range.key"
            :class="pricing_gap_chip_classes(range.key, 'gap')"
            @click="filter_by_pricing_gap(range.key)"
          >{{ range.label }}: {{ pricing_gap_counts[range.key] || 0 }}</span>
        </template>
        <span class="tenant-filter-sep">|</span>
        <template v-for="filter in pricing_special_filters">
          <span
            v-if="show_filter_chip(pricing_gap_counts[filter.key], selected_pricing_gap === filter.key)"
            :key="'special-' + filter.key"
            :class="pricing_gap_chip_classes(filter.key, 'special')"
            @click="filter_by_pricing_gap(filter.key)"
          >{{ filter.label }}: {{ pricing_gap_counts[filter.key] || 0 }}</span>
        </template>
      </div>
      </template>
      <div class="tenant-search-row">
        <span class="tenant-filter-label">Search</span>
        <input
          ref="tenantSearchInput"
          type="text"
          class="form-control form-control-sm tenant-search-input"
          placeholder="Name or user"
          @input="schedule_tenant_search"
          @keyup.enter="run_tenant_search"
        />
        <b-button
          size="sm"
          variant="primary"
          class="tenant-search-btn"
          title="Search"
          @click="run_tenant_search"
        >
          <i class="fa fa-search" aria-hidden="true"></i>
        </b-button>
        <span class="tenant-filter-sep">|</span>
        <span class="tenant-filter-label">Settings</span>
        <b-form-select
          size="sm"
          class="tenant-setting-key-select"
          v-model="setting_filter_key"
          :options="enabled_setting_options"
        />
        <b-form-select
          size="sm"
          class="tenant-setting-value-select"
          v-model="setting_filter_value"
          :options="setting_filter_value_options"
          :disabled="!setting_filter_key"
        />
        <b-button
          v-if="!billing_loaded"
          size="sm"
          variant="primary"
          class="tenant-billing-load-btn"
          title="Load Stripe and SMS billing data"
          :disabled="loading || billing_loading"
          @click="load_billing_data"
        >
          <i v-if="billing_loading" class="fa fa-spinner fa-spin" aria-hidden="true"></i>
          {{ billing_loading ? 'Loading billing…' : 'Load billing data' }}
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="tenant-reload-btn"
          title="Reload"
          :disabled="loading || billing_loading"
          @click="load_data"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading || billing_loading }" aria-hidden="true"></i>
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="tenant-download-csv-btn"
          title="Download filtered results as CSV"
          :disabled="loading || filtering_busy"
          @click="download_filtered_csv"
        >
          <i class="fa fa-download" aria-hidden="true"></i>
        </b-button>
      </div>
    </div>

      <b-table class="tenant-manager-table" :busy="loading || filtering_busy" sticky-header="80vh" striped bordered :items="items" :fields="visible_fields" responsive
             :current-page="currentPage"
             :per-page="perPage"
             :tbody-tr-class="billing_group_row_class"
             :sort-compare="table_sort_compare">

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>

        <template slot="last_actuals_request_time" slot-scope="row">
          {{ format_tenant_date(row_last_actuals_request_time(row.item)) }}
        </template>

        <template slot="last_booking_update_time" slot-scope="row">
          {{ format_tenant_date(row_last_booking_update_time(row.item)) }}
        </template>

        <template slot="vehicle_count" slot-scope="row">
          {{ row_vehicle_count(row.item) }}
        </template>

        <template slot="driver_count" slot-scope="row">
          {{ row_driver_count(row.item) }}
        </template>

        <template slot="tenant_name" slot-scope="row">
          <div v-if="is_billing_group_summary(row.item)" class="tenant-name-cell">
            <span class="tenant-billing-group-summary-name">
              {{ row.item.tenant_name }}
              <span class="tenant-billing-group-badge">{{ row.item.billing_group_size }} tenants</span>
            </span>
          </div>
          <div
            v-else
            class="tenant-name-cell"
            :class="{ 'tenant-billing-group-child-name': is_billing_group_tenant_row(row.item) }"
          >
            <div class="tenant-name-line">
              <span v-if="is_billing_group_tenant_row(row.item)" class="tenant-billing-group-marker">↳</span>
              <a
                v-if="row.item.tenant_id"
                :href="tenant_settings_url(row.item.tenant_id)"
                target="_blank"
                rel="noopener noreferrer"
                class="tenant-name-link"
                @click.stop
              >{{ row.item.tenant_name }}</a>
              <template v-else>{{ row.item.tenant_name }}</template>
            </div>
            <div class="tenant-id-line">{{ row.item.tenant_id }}</div>
          </div>
        </template>

        <template slot="platforms" slot-scope="row">
          <div class="tenant-platforms-cell">
            <span
              v-for="tenantType in row_platform_tenant_types(row.item)"
              :key="'tenant-type-' + tenantType"
              @click.stop="filter_by_status('tenant', tenantType)"
              :class="tenant_type_badge_classes(tenantType)"
            >{{ tenantType }}</span>
            <span
              v-for="telematicsType in row_platform_telematics_types(row.item)"
              :key="'telematics-type-' + telematicsType"
              @click.stop="filter_by_status('telematics', telematicsType)"
              :class="telematics_type_badge_classes(telematicsType)"
            >{{ telematicsType }}</span>
            <span
              v-for="maint in row_platform_maint_labels(row.item)"
              :key="'maint-' + maint.key"
              @click.stop="filter_by_status('maint', maint.key)"
              :class="maint_badge_classes(maint.key)"
            >{{ maint.label }}</span>
          </div>
        </template>

        <template slot="orgs" slot-scope="row">
          <div class="tenant-orgs-cell">
            <span
              v-for="org in row_motorcoach_organizations(row.item)"
              :key="'motorcoach-org-' + org"
              @click.stop="filter_by_status('motorcoachOrg', org)"
              :class="motorcoach_org_badge_classes(org)"
              :title="'Motorcoach organization: ' + org"
            >{{ org }}</span>
            <span v-if="!row_motorcoach_organizations(row.item).length">—</span>
          </div>
        </template>

        <template slot="last_login" slot-scope="row">
          <div v-if="row_latest_login(row.item)" class="tenant-last-login-cell">
            <div class="tenant-last-login-time">{{ format_last_login(row_latest_login(row.item).user.last_login_time) }}</div>
            <div class="tenant-last-login-user">{{ row_latest_login_user_label(row.item) }}</div>
          </div>
          <span v-else>—</span>
        </template>

        <template slot="features" slot-scope="row">
          <div class="tenant-features-cell" style="margin:10px">
            <template v-for="(label, key) in feature_filters">
              <span
                v-if="row_feature_enabled(row.item, key)"
                :key="key"
                :class="feature_filter_chip_classes(key)"
                :title="key"
                @click.stop="filter_by_status('feature', key)"
              >{{ label }}</span>
            </template>
          </div>
        </template>

        <template slot="pricing_diff" slot-scope="row">
          <div v-if="show_row_pricing_gap(row.item)" class="tenant-pricing-stack">
            <div
              v-if="!selected_feature"
              class="tenant-pricing-total-line tenant-pricing-cell"
              :class="pricing_diff_classes(row.item)"
              :title="pricing_diff_tooltip(row.item)"
            >{{ row_pricing_diff_display(row.item) }}</div>
            <div
              v-for="entry in row_pricing_diff_breakdown(row.item)"
              :key="'gap-' + entry.key"
              class="tenant-pricing-package-line"
              :class="pricing_diff_package_classes(entry.diff)"
              :title="pricing_diff_tooltip(row.item)"
            >{{ entry.label }} {{ format_signed_currency(entry.diff) }}</div>
          </div>
          <span v-else class="tenant-pricing-empty-cell"></span>
        </template>

        <template slot="monthly_pricing" slot-scope="row">
          <div v-if="show_row_pricing(row.item)" class="tenant-pricing-stack">
            <div v-if="!selected_feature" class="tenant-pricing-total-line tenant-pricing-override-row">
              <span class="tenant-pricing-computed">{{ base_monthly_pricing_display(row.item) }}</span>
              <b-form-input
                size="sm"
                class="tenant-pricing-override-input tenant-pricing-override-total"
                :class="{ 'tenant-pricing-override-active': has_monthly_pricing_override(row.item) }"
                :value="monthly_pricing_override_total_input_value(row.item)"
                :placeholder="monthly_pricing_total_placeholder(row.item)"
                readonly
                tabindex="-1"
              />
            </div>
            <div
              v-for="entry in base_monthly_pricing_breakdown(row.item)"
              :key="'monthly-' + entry.key"
              class="tenant-pricing-package-line tenant-pricing-override-row"
              :class="{ 'tenant-alerts-sms-over-estimate': alerts_sms_exceeds_estimate(row.item, entry) }"
              :title="monthly_package_tooltip(row.item, entry)"
            >
              <span class="tenant-pricing-computed">{{ format_monthly_package_display(entry) }}</span>
              <b-form-input
                size="sm"
                class="tenant-pricing-override-input"
                :class="{ 'tenant-pricing-override-active': has_monthly_pricing_override_package(row.item, entry.key) }"
                :value="monthly_pricing_override_package(row.item, entry.key)"
                :placeholder="monthly_pricing_package_placeholder(entry)"
                @input="set_monthly_pricing_override_package(row.item, entry.key, $event)"
              />
            </div>
            <div class="tenant-pricing-exclude-row">
              <b-form-checkbox
                :checked="is_pricing_excluded(row.item)"
                @change="set_pricing_excluded(row.item, $event)"
              >Skip $</b-form-checkbox>
            </div>
          </div>
          <span v-else class="tenant-pricing-empty-cell"></span>
        </template>

        <template slot="stripe_subscription" slot-scope="row">
          <div v-if="show_row_stripe_pricing(row.item)" class="tenant-pricing-stack">
            <div v-if="!selected_feature" class="tenant-pricing-total-line">
              <div
                v-if="row_stripe_customer_name(row.item)"
                class="tenant-stripe-customer-name"
              >{{ row_stripe_customer_name(row.item) }}</div>
              <a
                v-if="stripe_subscription_url(row.item)"
                :href="stripe_subscription_url(row.item)"
                target="_blank"
                rel="noopener noreferrer"
                class="tenant-stripe-subscription-link"
                :title="'Open ' + row_stripe_subscription_id(row.item) + ' in Stripe'"
              >{{ row_stripe_pricing_display(row.item) }}</a>
              <span v-else>{{ row_stripe_pricing_display(row.item) }}</span>
            </div>
            <div
              v-for="entry in row_stripe_pricing_breakdown(row.item)"
              :key="'stripe-' + entry.key"
              class="tenant-pricing-package-line"
              :class="{ 'tenant-stripe-unmapped': entry.unmapped }"
              :title="stripe_line_tooltip(row.item, entry)"
            >{{ entry.label }} {{ format_package_price(entry.price) }}</div>
          </div>
          <span v-else class="tenant-pricing-empty-cell"></span>
        </template>

      <template slot="details" slot-scope="row">
        <b-button
          v-if="is_billing_group_summary(row.item)"
          size="sm"
          variant="secondary"
          class="tenant-group-toggle-btn"
          @click.stop="toggle_billing_group(row.item.billing_group_key)"
        >{{ is_billing_group_expanded(row.item.billing_group_key) ? '-' : '+' }}</b-button>
        <b-button
          v-else
          size="sm"
          :variant="is_billing_group_tenant_row(row.item) ? 'secondary' : 'primary'"
          :class="is_billing_group_tenant_row(row.item) ? 'tenant-group-toggle-btn' : 'tenant-row-details-btn'"
          @click="row.toggleDetails"
        >{{ row.detailsShowing ? '-' : '+' }}</b-button>&nbsp;
      </template>

      <template slot="row-details" slot-scope="row">
        <b-card class="tenant-row-details-card">
          <table v-if="row_users(row.item).length" class="tenant-users-table tenant-users-table-no-hover" cellpadding="0" cellspacing="0">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Title</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(u, userIndex) in row_users(row.item)" :key="user_row_key(u, userIndex)">
                <td class="tenant-user-username">{{ u.username || '—' }}</td>
                <td class="tenant-user-name">{{ u.first_name }} {{ u.last_name }}</td>
                <td>{{ u.job_title || '—' }}</td>
                <td class="tenant-user-login">{{ format_last_login(u.last_login_time) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="tenant-row-details-empty">No users</div>
          <div v-if="!is_billing_group_summary(row.item)" class="tenant-row-details-capabilities">
            <span class="tenant-row-details-caps-label">Ops Desk</span>
            <b-form-checkbox
              v-for="cap in ops_desk_capabilities"
              :key="cap.key"
              :checked="tenant_feature_flag_enabled(row.item, cap.key)"
              @change="set_feature_flag(row.item, cap.key, $event)"
            >{{ cap.label }}</b-form-checkbox>
          </div>
          <div class="tenant-row-details-actions">
            <b-button size="sm" variant="outline-secondary" @click="toggle_row_json(row.item)">
              {{ is_row_json_visible(row.item) ? 'Hide JSON' : 'Show JSON' }}
            </b-button>
          </div>
          <pre v-if="is_row_json_visible(row.item)" class="tenant-row-details-json">{{ row.item }}</pre>
        </b-card>
      </template>

    </b-table>

  </div>
</template>
<script>
import {delete_booking, delete_tenant, get_all_tenants_config, get_tenant_billing_enrichment, send_sos_request, update_tenant_config_settings} from '../networking/apis'
    import {mapState} from "vuex";
    import moment from 'moment'

    // https://www.initekconsulting.com/pricing
    const PACKAGE_PRICING = {
      basic: {
        label: 'Basic',
        tiers: [{ maxVehicles: 9, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
      },
      actuals: {
        label: 'Actuals',
        tiers: [{ maxVehicles: 9, price: 100 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 200 }],
      },
      views_reports: {
        label: 'Reports',
        tiers: [{ maxVehicles: 9, price: 50 }, { maxVehicles: 49, price: 100 }, { maxVehicles: 99, price: 200 }],
      },
      alerts: {
        label: 'Alerts',
        tiers: [{ maxVehicles: 9, price: 50 }, { maxVehicles: 49, price: 150 }, { maxVehicles: 99, price: 250 }],
      },
      vsm: {
        label: 'VSM',
        tiers: [{ maxVehicles: 9, price: 50 }, { maxVehicles: 49, price: 100 }, { maxVehicles: 99, price: 200 }],
      },
      data: {
        label: 'Data',
        tiers: [{ maxVehicles: 9, price: 50 }, { maxVehicles: 49, price: 100 }, { maxVehicles: 99, price: 200 }],
      },
      maps: {
        label: 'Maps',
        defaultPrice: 0,
      },
    }
    const PACKAGE_ORDER = ['basic', 'actuals', 'views_reports', 'alerts', 'vsm', 'data', 'maps']
    const PRICING_GAP_POSITIVE_KEY = 'gap_positive'
    const PRICING_GAP_DOLLAR_RANGES = [
      { key: '1-50', label: '$1-50', min: 1, max: 50 },
      { key: '51-100', label: '$51-100', min: 51, max: 100 },
      { key: '101-200', label: '$101-200', min: 101, max: 200 },
      { key: '200+', label: '$200+', min: 200, max: null },
    ]
    const PRICING_GAP_SPECIAL_RANGES = [
      { key: PRICING_GAP_POSITIVE_KEY, label: 'Gap ≠ 0', nonZeroGap: true },
      { key: 'custom', label: 'Custom', custom: true },
      { key: 'no_stripe', label: 'No Stripe', noStripe: true },
      { key: 'sms_over', label: 'SMS Over', smsOver: true },
      { key: 'skip', label: 'Skip', skip: true },
    ]

    function create_empty_toolbar_state(featureFilterKeys, maintFilterKeys, vehicleTierKeys) {
      const feature_counts = {}
      const feature_stripe_totals = {}
      const feature_stripe_total_labels = {}
      for (const key of featureFilterKeys)
        feature_counts[key] = 0
      for (const key of featureFilterKeys) {
        feature_stripe_totals[key] = 0
        feature_stripe_total_labels[key] = ''
      }
      const vehicle_tier_counts = {}
      for (const tier of vehicleTierKeys)
        vehicle_tier_counts[tier.key] = 0
      const maint_counts = {}
      for (const key of maintFilterKeys)
        maint_counts[key] = 0
      const motorcoach_org_counts = {}
      for (const org of MOTORCOACH_ORGANIZATION_FILTERS)
        motorcoach_org_counts[org] = 0
      return {
        vehicle_cnt: 0,
        driver_cnt: 0,
        cm_cnt: 0,
        tbn_cnt: 0,
        fasttrak_cnt: 0,
        samsara_cnt: 0,
        saucon_cnt: 0,
        zonar_cnt: 0,
        motive_cnt: 0,
        none_cnt: 0,
        feature_counts,
        feature_stripe_totals,
        feature_stripe_total_labels,
        vehicle_tier_counts,
        maint_counts,
        motorcoach_org_counts,
        billing_group_cnt: 0,
        pricing_gap_counts: {},
        pricing_totals: { monthly: '—', stripe: '—', gap: '—' },
      }
    }

    const BILLING_GROUP_OVERRIDES = {
      'lamoille valley': 'Lamoille Valley',
      'custom coach of new england': 'Lamoille Valley',
      'peak touring': 'Lamoille Valley',
      'jefferson lines': 'Jefferson Lines',
      'karst stage': 'Jefferson Lines',
    }

    function billingGroupNamePrefix(name) {
      if (!name)
        return ''
      return String(name).replace(/\s*\([^)]*\)\s*$/g, '').trim()
    }
    const MAINT_FILTERS = {
      vehicle_status_management_fleetio_enabled: 'Fleetio',
      vehicle_status_management_rta_enabled: 'RTA',
      vehicle_status_management_tbn_status_enabled: 'TBN Maint',
    }
    const MOTORCOACH_ORGANIZATION_FILTERS = ['IMG', 'UMA', 'ABA', 'CBA', 'GNEMA']
    const VEHICLE_TIER_FILTERS = [
      { key: 'tier_1_9', label: '1-9', min: 1, max: 9 },
      { key: 'tier_10_49', label: '10-49', min: 10, max: 49 },
      { key: 'tier_50_99', label: '50-99', min: 50, max: 99 },
      { key: 'tier_100_plus', label: '100-249', min: 100, max: 249 },
      { key: 'tier_250_plus', label: '250+', min: 250, max: null },
    ]
    const FEATURE_PACKAGE_MAP = {
      basic_enabled: 'basic',
      actuals_enabled: 'actuals',
      alerts_enabled: 'alerts',
      views_reports_enabled: 'views_reports',
      vehicle_status_management_enabled: 'vsm',
      data_services_enabled: 'data',
      maps_enabled: 'maps',
    }
    // feature-filter keys that are not tenant fields but a roll-up of several.
    // driver manager is one package (scorecard + coaching portal), so the chip
    // counts a tenant once if either half is on.
    const DERIVED_FEATURE_FLAGS = {
      driver_manager_enabled: ['driver_scorecard_enabled', 'coaching_portal_enabled'],
    }
    const FEATURE_STRIPE_LABELS = {}
    const BILLABLE_STRIPE_SUBSCRIPTION_STATUSES = new Set(['active', 'past_due', 'trialing'])
    const NON_BILLABLE_STRIPE_SUBSCRIPTION_STATUSES = new Set([
      'canceled', 'cancelled', 'incomplete', 'incomplete_expired', 'unpaid', 'paused',
    ])
    const EXCLUDED_TENANT_IDS = new Set([
      'opv3-1234',
      'support-1df66d7a',
    ])
    const BILLING_TABLE_FIELD_KEYS = new Set([
      'monthly_pricing',
      'stripe_subscription',
      'pricing_diff',
    ])
    const BILLING_SESSION_KEY = 'tenant_manager_billing_loaded'

    export default {
      name: 'tenant_manager',
      data() {
        return {
          items: [],
          items_full: [],
          filtered_tenants: [],
          billing_group_index: {},
          expanded_billing_groups: {},
          expanded_row_json: {},
          pricing_save_timers: {},
          apply_filters_timer: null,
          filter_work_timer: null,
          toolbar_refresh_timer: null,
          filter_run_id: 0,
          filtering_busy: false,
          filter_snapshots: null,
          pricing_gap_context_by_key: null,
          billing_unit_by_key: {},
          toolbar_rebuild_timer: null,
          tenant_search_debounce_timer: null,
          pricing_unit_cache: {},
          toolbar_state: create_empty_toolbar_state(
            ['basic_enabled', 'actuals_enabled', 'alerts_enabled', 'views_reports_enabled',
              'vehicle_status_management_enabled', 'data_services_enabled', 'maps_enabled', 'bus_sos_enabled'],
            Object.keys(MAINT_FILTERS),
            VEHICLE_TIER_FILTERS,
          ),
          enabled_setting_options: [{ value: null, text: '-- setting --' }],
          summary: {},
          loading: true,
          billing_loaded: false,
          billing_loading: false,
          tenant_search_filter: null,
          setting_filter_key: null,
          setting_filter_value: null,
          selected_tenant_type: null,
          selected_telematics_type: null,
          selected_maint: null,
          selected_motorcoach_org: null,
          selected_feature: null,
          selected_vehicle_tier: null,
          selected_pricing_gap: null,
          selected_billing_groups_only: false,
          maint_filters: MAINT_FILTERS,
          motorcoach_organization_filters: MOTORCOACH_ORGANIZATION_FILTERS,
          vehicle_tier_filters: VEHICLE_TIER_FILTERS,
          feature_filters: {
            basic_enabled: 'Basic',
            actuals_enabled: 'Actuals',
            alerts_enabled: 'Alerts',
            views_reports_enabled: 'Reports',
            vehicle_status_management_enabled: 'VSM',
            data_services_enabled: 'Data',
            maps_enabled: 'Maps',
            bus_sos_enabled: 'SOS',
            // FI-2083. the two ops desk packages, matching how they are sold on
            // initekconsulting.com/pricing (separate SKUs, not one bundle).
            // neither has a FEATURE_PACKAGE_MAP / FEATURE_STRIPE_LABELS entry
            // yet, so the stripe lookup resolves to null and the chips show a
            // count with no dollar suffix. add the label mapping when ben wires
            // the prices and they roll up here on their own.
            ops_desk_enabled: 'ERM',
            driver_manager_enabled: 'Driver Mgr',
          },
          // the ops desk umbrella as staff enable it, one row-details block.
          // driver manager is sold as one package but rides two flags, so the
          // chip above is derived (DERIVED_FEATURE_FLAGS) while these stay the
          // real fields we write.
          ops_desk_capabilities: [
            { key: 'ops_desk_enabled', label: 'Event Response Manager' },
            { key: 'driver_scorecard_enabled', label: 'Driver Scorecard' },
            { key: 'coaching_portal_enabled', label: 'Coaching Portal' },
          ],
          boolean_options: [
            { text: 'True', value: 'True' },
            { text: 'False', value: 'False' },
          ],
          totalRows: 1,
          currentPage: 1,
          perPage: 100,
          pages: [],
          fields: [
            { key: 'details', label: '', sortable: false, thStyle: { width: "52px" }},
            {
              key: 'tenant_name',
              label: 'Tenant',
              sortable: true,
              thStyle: { width: "240px", minWidth: "200px" },
              tdClass: 'tenant-data-td tenant-name-td',
              thClass: 'tenant-data-th tenant-name-th',
            },
            {
              key: 'platforms',
              label: 'Platforms',
              sortable: true,
              sortByFormatted: true,
              formatter: (value, key, item) => this.row_platforms_sort_label(item),
              thStyle: { width: "130px" },
              tdClass: 'tenant-data-td tenant-platforms-td',
              thClass: 'tenant-data-th',
            },
            {
              key: 'orgs',
              label: 'Orgs',
              sortable: true,
              sortByFormatted: true,
              formatter: (value, key, item) => this.row_orgs_sort_label(item),
              thStyle: { width: "72px" },
              tdClass: 'tenant-data-td tenant-orgs-td',
              thClass: 'tenant-data-th',
            },
            {
              key: 'last_login',
              label: 'Last Login',
              sortable: true,
              sortByFormatted: true,
              formatter: (value, key, item) => this.row_latest_login_sort_value(item),
              thStyle: { width: "130px" },
              tdClass: 'tenant-data-td tenant-last-login-td',
              thClass: 'tenant-data-th',
            },
            { key: 'last_actuals_request_time', label: 'Last Actuals Push', sortable: true, thStyle: { width: "100px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'last_booking_update_time', label: 'Last Booking Update', sortable: true, thStyle: { width: "100px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'vehicle_count', label: 'Vehicles', sortable: true, thStyle: { width: "50px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            { key: 'driver_count', label: 'Drivers', sortable: true, thStyle: { width: "50px" }, tdClass: 'tenant-data-td', thClass: 'tenant-data-th' },
            {
              key: 'monthly_pricing',
              label: 'Estimated $',
              sortable: true,
              sortByFormatted: true,
              formatter: (value, key, item) => this.show_row_pricing(item) ? this.row_monthly_pricing_total(item) : null,
              thStyle: { width: "165px" },
              tdClass: 'tenant-data-td tenant-pricing-td',
              thClass: 'tenant-data-th',
            },
            {
              key: 'stripe_subscription',
              label: 'In Stripe $',
              sortable: true,
              sortByFormatted: true,
              formatter: (value, key, item) => this.show_row_stripe_pricing(item) ? this.row_stripe_amount(item) : null,
              thStyle: { width: "95px" },
              tdClass: 'tenant-data-td tenant-pricing-td',
              thClass: 'tenant-data-th',
            },
            {
              key: 'pricing_diff',
              label: 'Diff $',
              sortable: true,
              sortByFormatted: true,
              formatter: (value, key, item) => {
                if (!this.show_row_pricing_gap(item))
                  return null
                const diff = this.row_pricing_diff(item)
                return diff == null ? null : Math.abs(diff)
              },
              thStyle: { width: "90px" },
              tdClass: 'tenant-data-td tenant-pricing-td',
              thClass: 'tenant-data-th',
            },
            { key: 'features', sortable: true, thStyle: { width: "220px", minWidth: "180px" }, tdClass: 'tenant-features-td', thClass: 'tenant-features-th' },
          ]

        }
      },
      async created() {
        await this.load_data()
        const billingQuery = this.$route && this.$route.query && this.$route.query.billing
        const sessionBilling = typeof window !== 'undefined'
          && window.sessionStorage
          && window.sessionStorage.getItem(BILLING_SESSION_KEY) === 'true'
        if (billingQuery === '1' || billingQuery === 'true' || sessionBilling)
          await this.load_billing_data()
      },
      methods: {
        api_response_payload(value) {
          const body = value && value.data
          if (!body)
            return {}
          return body.data || body
        },
        async load_data() {
          this.billing_loaded = false
          this.billing_loading = false
          if (typeof window !== 'undefined' && window.sessionStorage)
            window.sessionStorage.removeItem(BILLING_SESSION_KEY)
          await this.load_base_data()
        },
        async load_base_data() {
          this.loading = true
          try {
            const { value } = await get_all_tenants_config()
            const data = this.api_response_payload(value)
            this.summary = data.summary
            this.items_full = (data.results || []).filter(
              item => !EXCLUDED_TENANT_IDS.has(item.tenant_id),
            )
            this.build_billing_group_index()
            this.build_tenant_search_indexes()
            this.enabled_setting_options = this.build_enabled_setting_options()
            this.pricing_unit_cache = {}
            this.billing_unit_by_key = {}
            this.apply_filters(true)
          } catch(e) {
            this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
          }

          this.loading = false
        },
        merge_billing_enrichment(enrichment) {
          const tenants = (enrichment && enrichment.tenants) || {}
          for (const item of this.items_full) {
            const billingFields = tenants[item.tenant_id]
            if (!billingFields)
              continue
            for (const [field, value] of Object.entries(billingFields))
              this.$set(item, field, value)
          }
        },
        async load_billing_data() {
          if (this.billing_loading || this.billing_loaded || !this.items_full.length)
            return

          this.billing_loading = true
          try {
            const { value } = await get_tenant_billing_enrichment()
            this.merge_billing_enrichment(this.api_response_payload(value))
            this.build_billing_group_index()
            this.refresh_all_pricing_caches()
            this.rebuild_billing_unit_index()
            this.apply_filters(true)
            this.billing_loaded = true
            if (typeof window !== 'undefined' && window.sessionStorage)
              window.sessionStorage.setItem(BILLING_SESSION_KEY, 'true')
          } catch(e) {
            this.$emit('showAlert', 'error loading billing data, try again or contact support if needed', 'danger', 5000)
          }

          this.billing_loading = false
        },
        tenant_type_badge_classes(value) {
          const classes = ['tenant-chip', 'tenant-filter-clickable']
          if (value === 'CM')
            classes.push('tenant-type-cm')
          else if (value === 'TBN')
            classes.push('tenant-type-tbn')
          else if (value === 'FASTTRAK')
            classes.push('tenant-type-fasttrak')
          if (this.selected_tenant_type === value)
            classes.push('tenant-chip-selected')
          return classes
        },
        telematics_type_badge_classes(value) {
          const classes = ['tenant-chip', 'tenant-filter-clickable']
          if (value === 'SAMSARA')
            classes.push('tenant-telematics-samsara')
          else if (value === 'SAUCON')
            classes.push('tenant-telematics-saucon')
          else if (value === 'ZONAR')
            classes.push('tenant-telematics-zonar')
          else if (value === 'MOTIVE')
            classes.push('tenant-telematics-motive')
          else if (value === 'NONE')
            classes.push('tenant-telematics-none')
          if (this.selected_telematics_type === value)
            classes.push('tenant-chip-selected')
          return classes
        },
        maint_badge_classes(key) {
          const classes = ['tenant-chip', 'tenant-filter-clickable']
          if (key === 'vehicle_status_management_fleetio_enabled')
            classes.push('tenant-maint-fleetio')
          else if (key === 'vehicle_status_management_rta_enabled')
            classes.push('tenant-maint-rta')
          else if (key === 'vehicle_status_management_tbn_status_enabled')
            classes.push('tenant-maint-tbn')
          if (this.selected_maint === key)
            classes.push('tenant-chip-selected')
          return classes
        },
        motorcoach_org_badge_classes(org) {
          const classes = ['tenant-chip', 'tenant-filter-clickable']
          if (org === 'IMG')
            classes.push('tenant-org-img')
          else if (org === 'UMA')
            classes.push('tenant-org-uma')
          else if (org === 'ABA')
            classes.push('tenant-org-aba')
          else if (org === 'CBA')
            classes.push('tenant-org-cba')
          else if (org === 'GNEMA')
            classes.push('tenant-org-gnema')
          else
            classes.push('tenant-chip-neutral')
          if (this.selected_motorcoach_org === org)
            classes.push('tenant-chip-selected')
          return classes
        },
        tenant_has_motorcoach_org(item, org) {
          return this.row_motorcoach_organizations(item).includes(org)
        },
        vehicle_tier_badge_classes(tierKey) {
          const classes = ['tenant-chip', 'tenant-filter-clickable', 'tenant-chip-vehicle-tier']
          if (tierKey === 'tier_1_9')
            classes.push('tenant-tier-1-9')
          else if (tierKey === 'tier_10_49')
            classes.push('tenant-tier-10-49')
          else if (tierKey === 'tier_50_99')
            classes.push('tenant-tier-50-99')
          else if (tierKey === 'tier_100_plus')
            classes.push('tenant-tier-100-plus')
          else if (tierKey === 'tier_250_plus')
            classes.push('tenant-tier-250-plus')
          if (this.selected_vehicle_tier === tierKey)
            classes.push('tenant-chip-selected')
          return classes
        },
        vehicle_tier_filter_for_key(tierKey) {
          return VEHICLE_TIER_FILTERS.find(tier => tier.key === tierKey) || null
        },
        vehicle_tier_key_for_count(vehicleCount) {
          const count = Number(vehicleCount)
          if (!Number.isFinite(count) || count < 1)
            return null
          for (const tier of VEHICLE_TIER_FILTERS) {
            if (tier.max == null && count >= tier.min)
              return tier.key
            if (tier.max != null && count >= tier.min && count <= tier.max)
              return tier.key
          }
          return null
        },
        vehicle_count_for_billing_item(item) {
          const members = this.billing_group_members(item.billing_group_key)
          if (members.length)
            return this.billing_group_vehicle_count(members)
          return this.tenant_vehicle_count(item)
        },
        tenant_matches_vehicle_tier(item, tierKey) {
          const tier = this.vehicle_tier_filter_for_key(tierKey)
          if (!tier)
            return false
          const count = this.vehicle_count_for_billing_item(item)
          if (tier.max == null)
            return count >= tier.min
          return count >= tier.min && count <= tier.max
        },
        show_filter_chip(count, isSelected) {
          return (count || 0) > 0 || isSelected
        },
        feature_filter_chip_classes(key) {
          return [
            'tenant-chip',
            'tenant-chip-feature',
            'tenant-filter-clickable',
            { 'tenant-chip-selected': this.selected_feature === key },
          ]
        },
        pricing_gap_chip_classes(rangeKey, type) {
          return [
            'tenant-chip',
            'tenant-filter-clickable',
            type === 'special' ? 'tenant-chip-pricing-special' : 'tenant-chip-pricing-gap',
            { 'tenant-chip-selected': this.selected_pricing_gap === rangeKey },
          ]
        },
        tenant_settings_url(tenantId) {
          if (!tenantId)
            return '#'
          return './#/settings?tenant_id=' + encodeURIComponent(tenantId)
        },
        filter_by_status(type, value) {
          if (type == null) {
            this.selected_tenant_type = null
            this.selected_telematics_type = null
            this.selected_maint = null
            this.selected_motorcoach_org = null
            this.selected_feature = null
            this.selected_vehicle_tier = null
            this.selected_pricing_gap = null
            this.selected_billing_groups_only = false
          }

          if (type == 'tenant') {
            if (this.selected_tenant_type == value)
              this.selected_tenant_type = null
            else
              this.selected_tenant_type = value
          }

          if (type == 'telematics') {
            if (this.selected_telematics_type == value)
              this.selected_telematics_type = null
            else
              this.selected_telematics_type = value
          }

          if (type == 'maint') {
            if (this.selected_maint == value)
              this.selected_maint = null
            else
              this.selected_maint = value
          }

          if (type == 'motorcoachOrg') {
            if (this.selected_motorcoach_org == value)
              this.selected_motorcoach_org = null
            else
              this.selected_motorcoach_org = value
          }

          if (type == 'feature') {
            if (this.selected_feature == value)
              this.selected_feature = null
            else
              this.selected_feature = value
          }

          if (type == 'vehicleTier') {
            if (this.selected_vehicle_tier == value)
              this.selected_vehicle_tier = null
            else
              this.selected_vehicle_tier = value
          }

          this.apply_filters(true)
        },
        filter_by_pricing_gap(rangeKey) {
          if (this.selected_pricing_gap === rangeKey)
            this.selected_pricing_gap = null
          else
            this.selected_pricing_gap = rangeKey
          this.apply_filters(true)
        },
        filter_by_billing_groups_only() {
          this.selected_billing_groups_only = !this.selected_billing_groups_only
          this.apply_filters(true)
        },
        is_multi_tenant_billing_group(item) {
          return (item.billing_group_size || 0) > 1
        },
        tenant_search_draft() {
          const input = this.$refs.tenantSearchInput
          return input ? (input.value || '').trim() : ''
        },
        schedule_tenant_search() {
          if (this.tenant_search_debounce_timer)
            clearTimeout(this.tenant_search_debounce_timer)
          this.tenant_search_debounce_timer = setTimeout(() => {
            this.tenant_search_debounce_timer = null
            this.run_tenant_search()
          }, 2000)
        },
        run_tenant_search() {
          if (this.tenant_search_debounce_timer) {
            clearTimeout(this.tenant_search_debounce_timer)
            this.tenant_search_debounce_timer = null
          }
          const next = this.tenant_search_draft()
          const current = (this.tenant_search_filter || '').trim()
          if (next === current)
            return
          this.tenant_search_filter = next || null
          this.apply_filters(true)
        },
        apply_filters(immediate, debounceMs) {
          if (this.apply_filters_timer) {
            clearTimeout(this.apply_filters_timer)
            this.apply_filters_timer = null
          }
          if (immediate) {
            this.schedule_filter_work(0)
            return
          }
          const delay = debounceMs == null ? 200 : debounceMs
          this.apply_filters_timer = setTimeout(() => {
            this.apply_filters_timer = null
            this.schedule_filter_work(0)
          }, delay)
        },
        invalidate_filter_snapshots() {
          this.filter_snapshots = null
          this.pricing_gap_context_by_key = null
        },
        schedule_filter_work(delayMs) {
          if (this.filter_work_timer) {
            clearTimeout(this.filter_work_timer)
            this.filter_work_timer = null
          }
          this.filtering_busy = true
          this.filter_run_id += 1
          const runId = this.filter_run_id
          const run = () => {
            this.filter_work_timer = null
            if (runId !== this.filter_run_id)
              return
            this.apply_filters_now()
          }
          if (delayMs > 0) {
            this.filter_work_timer = setTimeout(run, delayMs)
            return
          }
          if (typeof window !== 'undefined' && window.requestAnimationFrame)
            window.requestAnimationFrame(run)
          else
            run()
        },
        apply_filters_now() {
          const runId = this.filter_run_id
          const snapshots = this.rebuild_filter_snapshots()
          this.filter_snapshots = snapshots
          const items = this.sort_by_billing_group(snapshots.afterGap)
          this.filtered_tenants = items
          this.items = this.build_display_items(items)
          this.filtering_busy = false
          this.schedule_toolbar_rebuild(snapshots, runId)
        },
        schedule_toolbar_rebuild(snapshots, runId) {
          if (this.toolbar_rebuild_timer) {
            clearTimeout(this.toolbar_rebuild_timer)
            this.toolbar_rebuild_timer = null
          }
          const rebuild = () => {
            this.toolbar_rebuild_timer = null
            if (runId != null && runId !== this.filter_run_id)
              return
            this.rebuild_toolbar_state(snapshots)
          }
          if (typeof window !== 'undefined' && window.requestIdleCallback) {
            this.toolbar_rebuild_timer = window.requestIdleCallback(rebuild, { timeout: 120 })
            return
          }
          if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(rebuild)
            return
          }
          rebuild()
        },
        filter_layer_for_counts(exclude = {}) {
          const snapshots = this.filter_snapshots
          if (!snapshots)
            return this.filter_items_for_counts(exclude)

          if (!exclude.pricingGap && this.selected_pricing_gap)
            return snapshots.afterGap
          if (!exclude.billingGroups && this.selected_billing_groups_only)
            return snapshots.afterBillingGroups
          if (!exclude.setting && this.setting_filter_key && this.setting_filter_value
            && this.setting_filter_value.trim())
            return snapshots.afterSetting
          if (!exclude.search && this.tenant_search_filter && this.tenant_search_filter.trim())
            return snapshots.afterSearch
          if (!exclude.vehicleTier && this.selected_vehicle_tier)
            return snapshots.afterVehicleTier
          if (!exclude.feature && this.selected_feature)
            return snapshots.afterFeature
          if (!exclude.maint && this.selected_maint)
            return snapshots.afterMaint
          if (!exclude.motorcoachOrg && this.selected_motorcoach_org)
            return snapshots.afterMotorcoachOrg
          if (!exclude.telematics && this.selected_telematics_type)
            return snapshots.afterTelematics
          if (!exclude.tenantType && this.selected_tenant_type)
            return snapshots.afterTenantType
          return snapshots.start
        },
        filter_items_for_counts(exclude = {}) {
          let items = this.items_full

          if (this.selected_tenant_type && !exclude.tenantType)
            items = items.filter(item => item.tenant_type == this.selected_tenant_type)

          if (this.selected_telematics_type && !exclude.telematics)
            items = items.filter(item => item.telematics_type == this.selected_telematics_type)

          if (this.selected_maint && !exclude.maint)
            items = items.filter(item => this.tenant_maint_enabled(item, this.selected_maint))

          if (this.selected_motorcoach_org && !exclude.motorcoachOrg)
            items = items.filter(item => this.tenant_has_motorcoach_org(item, this.selected_motorcoach_org))

          if (this.selected_feature && !exclude.feature)
            items = items.filter(item => this.tenant_feature_flag_enabled(item, this.selected_feature))

          if (this.selected_vehicle_tier && !exclude.vehicleTier)
            items = items.filter(item => this.tenant_matches_vehicle_tier(item, this.selected_vehicle_tier))

          const search = this.tenant_search_filter && this.tenant_search_filter.trim()
          if (search && !exclude.search) {
            const lower = search.toLowerCase()
            items = items.filter(item => item._name_username_blob && item._name_username_blob.indexOf(lower) !== -1)
          }

          const settingValue = this.setting_filter_value && this.setting_filter_value.trim()
          if (this.setting_filter_key && settingValue && !exclude.setting)
            items = items.filter(item => this.tenant_has_key_value(item, this.setting_filter_key, settingValue))

          if (this.selected_billing_groups_only && !exclude.billingGroups)
            items = items.filter(item => this.is_multi_tenant_billing_group(item))

          if (this.selected_pricing_gap && !exclude.pricingGap)
            items = this.filter_items_by_pricing_gap(items, this.selected_pricing_gap)

          return items
        },
        items_for_counts(exclude = {}) {
          return this.filter_layer_for_counts(exclude)
        },
        filter_stage(items, predicate) {
          if (!predicate)
            return items
          const next = []
          for (let i = 0; i < items.length; i++) {
            if (predicate(items[i]))
              next.push(items[i])
          }
          return next
        },
        rebuild_filter_snapshots() {
          const snapshots = { start: this.items_full }
          let stage = this.items_full

          stage = this.filter_stage(stage, this.selected_tenant_type
            ? item => item.tenant_type == this.selected_tenant_type
            : null)
          snapshots.afterTenantType = stage

          stage = this.filter_stage(stage, this.selected_telematics_type
            ? item => item.telematics_type == this.selected_telematics_type
            : null)
          snapshots.afterTelematics = stage

          stage = this.filter_stage(stage, this.selected_maint
            ? item => this.tenant_maint_enabled(item, this.selected_maint)
            : null)
          snapshots.afterMaint = stage

          stage = this.filter_stage(stage, this.selected_motorcoach_org
            ? item => this.tenant_has_motorcoach_org(item, this.selected_motorcoach_org)
            : null)
          snapshots.afterMotorcoachOrg = stage

          stage = this.filter_stage(stage, this.selected_feature
            ? item => this.tenant_feature_flag_enabled(item, this.selected_feature)
            : null)
          snapshots.afterFeature = stage

          stage = this.filter_stage(stage, this.selected_vehicle_tier
            ? item => this.tenant_matches_vehicle_tier(item, this.selected_vehicle_tier)
            : null)
          snapshots.afterVehicleTier = stage

          const search = this.tenant_search_filter && this.tenant_search_filter.trim()
          if (search) {
            const lower = search.toLowerCase()
            stage = this.filter_stage(stage, item =>
              item._name_username_blob && item._name_username_blob.indexOf(lower) !== -1)
          }
          snapshots.afterSearch = stage

          const settingValue = this.setting_filter_value && this.setting_filter_value.trim()
          stage = this.filter_stage(stage, this.setting_filter_key && settingValue
            ? item => this.tenant_has_key_value(item, this.setting_filter_key, settingValue)
            : null)
          snapshots.afterSetting = stage

          stage = this.filter_stage(stage, this.selected_billing_groups_only
            ? item => this.is_multi_tenant_billing_group(item)
            : null)
          snapshots.afterBillingGroups = stage

          if (this.has_active_filters_excluding_pricing_gap())
            this.pricing_gap_context_by_key = this.build_pricing_gap_context_by_key(stage)
          else
            this.pricing_gap_context_by_key = null

          snapshots.afterGap = this.selected_pricing_gap
            ? this.filter_items_by_pricing_gap(stage, this.selected_pricing_gap)
            : stage

          return snapshots
        },
        filter_items_by_pricing_gap(items, rangeKey) {
          const allowedKeys = new Set()
          const checkedKeys = new Set()
          const result = []

          for (let i = 0; i < items.length; i++) {
            const item = items[i]
            const key = item.billing_group_key
            if (!checkedKeys.has(key)) {
              checkedKeys.add(key)
              if (this.pricing_gap_matches_range(item, rangeKey))
                allowedKeys.add(key)
            }
            if (allowedKeys.has(key))
              result.push(item)
          }
          return result
        },
        build_pricing_gap_context_by_key(items) {
          const membersByKey = {}
          for (const item of items) {
            const key = item.billing_group_key
            if (!key)
              continue
            if (!membersByKey[key])
              membersByKey[key] = []
            membersByKey[key].push(item)
          }

          const map = {}
          const useFiltered = this.has_active_filters_excluding_pricing_gap()
          for (const [key, members] of Object.entries(membersByKey)) {
            if (!useFiltered) {
              const cached = this.pricing_unit_cache[key]
              if (cached)
                map[key] = { cached, unit: this.pricing_unit_for_billing_key(key) }
              continue
            }

            const unit = members.length > 1
              ? this.build_billing_group_summary(key, members)
              : members[0]
            map[key] = {
              unit,
              cached: {
                monthly: this.row_monthly_pricing_total(unit),
                stripe: this.row_stripe_amount(unit),
                gap: this.pricing_gap_for_unit(unit),
                excluded: this.is_pricing_excluded(unit),
                custom: this.is_custom_pricing_unit(unit),
                smsOver: this.tenant_sms_exceeds_alerts_estimate(unit),
              },
            }
          }
          return map
        },
        rebuild_toolbar_state(snapshots) {
          const state = create_empty_toolbar_state(
            Object.keys(this.feature_filters),
            Object.keys(this.maint_filters),
            this.vehicle_tier_filters,
          )

          const countMatching = (baseItems, predicate) => {
            let count = 0
            for (let i = 0; i < baseItems.length; i++) {
              if (predicate(baseItems[i]))
                count++
            }
            return count
          }
          const opsBase = this.filter_layer_for_counts({ tenantType: true })
          const telematicsBase = this.filter_layer_for_counts({ telematics: true })
          const maintBase = this.filter_layer_for_counts({ maint: true })
          const motorcoachOrgBase = this.filter_layer_for_counts({ motorcoachOrg: true })
          const featureBase = this.filter_layer_for_counts({ feature: true })
          const vehicleTierBase = this.filter_layer_for_counts({ vehicleTier: true })
          const groupsBase = this.filter_layer_for_counts({ billingGroups: true })
          const gapBase = snapshots.afterBillingGroups

          const groupKeys = new Set()
          for (const item of groupsBase) {
            if (this.is_multi_tenant_billing_group(item) && item.billing_group_key)
              groupKeys.add(item.billing_group_key)
          }
          state.billing_group_cnt = groupKeys.size

          state.fasttrak_cnt = countMatching(opsBase, item => item.tenant_type == 'FASTTRAK')
          state.cm_cnt = countMatching(opsBase, item => item.tenant_type == 'CM')
          state.tbn_cnt = countMatching(opsBase, item => item.tenant_type == 'TBN')
          state.samsara_cnt = countMatching(telematicsBase, item => item.telematics_type == 'SAMSARA')
          state.saucon_cnt = countMatching(telematicsBase, item => item.telematics_type == 'SAUCON')
          state.zonar_cnt = countMatching(telematicsBase, item => item.telematics_type == 'ZONAR')
          state.motive_cnt = countMatching(telematicsBase, item => item.telematics_type == 'MOTIVE')
          state.none_cnt = countMatching(telematicsBase, item => item.telematics_type == 'NONE')

          for (const item of maintBase) {
            for (const key of Object.keys(this.maint_filters)) {
              if (this.tenant_maint_enabled(item, key))
                state.maint_counts[key]++
            }
          }

          for (const item of motorcoachOrgBase) {
            for (const org of this.row_motorcoach_organizations(item)) {
              if (state.motorcoach_org_counts[org] != null)
                state.motorcoach_org_counts[org]++
            }
          }

          for (const item of featureBase) {
            for (const key of Object.keys(this.feature_filters)) {
              if (this.tenant_feature_flag_enabled(item, key))
                state.feature_counts[key]++
            }
          }

          const featureStripeSeen = {}
          for (const key of Object.keys(this.feature_filters))
            featureStripeSeen[key] = new Set()

          for (const item of featureBase) {
            const billingKey = item.billing_group_key
            const billingUnit = this.billing_unit_by_key[billingKey]
              || this.pricing_billing_unit_for_tenant(item)
            for (const featureKey of Object.keys(this.feature_filters)) {
              if (!this.tenant_feature_flag_enabled(item, featureKey))
                continue
              if (featureStripeSeen[featureKey].has(billingKey))
                continue
              featureStripeSeen[featureKey].add(billingKey)
              if (this.is_pricing_excluded(billingUnit))
                continue
              const amount = this.stripe_feature_amount(billingUnit, featureKey)
              if (amount != null)
                state.feature_stripe_totals[featureKey] += amount
            }
          }
          for (const key of Object.keys(this.feature_filters))
            state.feature_stripe_total_labels[key] = this.format_stripe_total_thousands(state.feature_stripe_totals[key])

          const tierSeen = new Set()
          for (const item of vehicleTierBase) {
            const key = item.billing_group_key
            if (tierSeen.has(key))
              continue
            tierSeen.add(key)
            const tierKey = this.vehicle_tier_key_for_count(this.vehicle_count_for_billing_item(item))
            if (tierKey)
              state.vehicle_tier_counts[tierKey]++
          }

          for (const range of this.pricing_gap_ranges)
            state.pricing_gap_counts[range.key] = 0

          const gapSeen = new Set()
          for (const item of gapBase) {
            const key = item.billing_group_key
            if (gapSeen.has(key))
              continue
            gapSeen.add(key)
            const ctx = this.pricing_gap_context_for_item(item)
            if (!ctx)
              continue
            for (const range of this.pricing_gap_ranges) {
              if (this.pricing_gap_matches_ctx(ctx, range))
                state.pricing_gap_counts[range.key]++
            }
          }

          const visibleByKey = {}
          for (const tenant of snapshots.afterGap) {
            const key = tenant.billing_group_key
            if (!visibleByKey[key])
              visibleByKey[key] = []
            visibleByKey[key].push(tenant)
          }
          const vehicleGroupsSeen = new Set()
          const driverGroupsSeen = new Set()
          for (const tenant of snapshots.afterGap) {
            const key = tenant.billing_group_key
            const visibleMembers = visibleByKey[key] || []
            if (visibleMembers.length > 1) {
              if (vehicleGroupsSeen.has(key))
                continue
              vehicleGroupsSeen.add(key)
              state.vehicle_cnt += this.billing_group_vehicle_count(visibleMembers)
              if (!driverGroupsSeen.has(key)) {
                driverGroupsSeen.add(key)
                state.driver_cnt += this.billing_group_driver_count(visibleMembers)
              }
            } else {
              state.vehicle_cnt += this.tenant_vehicle_count(tenant)
              state.driver_cnt += this.tenant_driver_count(tenant)
            }
          }

          let monthly = 0
          let stripe = 0
          let gap = 0
          let hasMonthly = false
          let hasStripe = false
          let hasGap = false
          const pricingSeen = new Set()
          const selectedFeature = this.selected_feature
          const packageKey = this.selected_feature_package_key()
          const stripeLabelKey = selectedFeature ? this.feature_stripe_label_key(selectedFeature) : null

          for (const tenant of snapshots.afterGap) {
            const key = tenant.billing_group_key
            if (pricingSeen.has(key))
              continue
            pricingSeen.add(key)

            if (this.is_pricing_excluded(tenant))
              continue

            if (selectedFeature && stripeLabelKey) {
              const billingUnit = this.billing_unit_by_key[key] || this.pricing_billing_unit_for_tenant(tenant)
              const monthlyBreakdown = this.resolve_monthly_pricing(billingUnit).breakdown
              const monthlyEntry = packageKey
                ? monthlyBreakdown.find(entry => entry.key === packageKey)
                : null
              const stripeBreakdown = this.stripe_pricing_breakdown(billingUnit)
              const stripeEntry = stripeBreakdown.find(
                entry => this.normalize_package_label(entry.label) === stripeLabelKey,
              )
              const monthlyAmount = monthlyEntry ? monthlyEntry.price : null
              const stripeAmount = stripeEntry ? stripeEntry.price : null
              if (monthlyAmount != null && monthlyAmount !== -1) {
                monthly += monthlyAmount
                hasMonthly = true
              }
              if (stripeAmount != null) {
                stripe += stripeAmount
                hasGap = true
                hasStripe = true
                if (monthlyAmount != null && monthlyAmount !== -1)
                  gap += stripeAmount - monthlyAmount
                else
                  gap += stripeAmount
              } else if (monthlyAmount != null && monthlyAmount !== -1) {
                hasGap = true
                gap -= monthlyAmount
              }
              continue
            }

            const cached = this.pricing_unit_cache[key]
            if (!cached || cached.excluded)
              continue
            if (cached.monthly != null && cached.monthly !== -1) {
              monthly += cached.monthly
              hasMonthly = true
            }
            if (cached.stripe != null) {
              stripe += cached.stripe
              hasStripe = true
            }
            if (cached.gap != null) {
              gap += cached.gap
              hasGap = true
            }
          }

          state.pricing_totals = {
            monthly: hasMonthly ? '$' + monthly + '/mo' : '—',
            stripe: hasStripe ? '$' + stripe + '/mo' : '—',
            gap: hasGap ? this.format_signed_currency(gap) : '—',
          }

          this.toolbar_state = state
        },
        build_display_items(tenants) {
          const membersByKey = {}
          for (const tenant of tenants) {
            const key = tenant.billing_group_key
            if (!membersByKey[key])
              membersByKey[key] = []
            membersByKey[key].push(tenant)
          }

          const result = []
          const processedGroups = new Set()

          for (const tenant of tenants) {
            const key = tenant.billing_group_key
            const visibleMembers = membersByKey[key] || []
            const size = visibleMembers.length

            if (size > 1) {
              if (!processedGroups.has(key)) {
                processedGroups.add(key)
                const summary = this.build_billing_group_summary(key, visibleMembers)
                if (this.is_billing_group_expanded(key))
                  summary._showDetails = true
                result.push(summary)
              }
              if (this.is_billing_group_expanded(key))
                result.push(tenant)
            } else {
              result.push(tenant)
            }
          }

          return result
        },
        has_active_filters_excluding_pricing_gap() {
          return !!(
            this.selected_tenant_type
            || this.selected_telematics_type
            ||             this.selected_maint
            || this.selected_motorcoach_org
            || this.selected_feature
            || this.selected_vehicle_tier
            || this.selected_billing_groups_only
            || (this.tenant_search_filter && this.tenant_search_filter.trim())
            || (this.setting_filter_key && this.setting_filter_value && this.setting_filter_value.trim())
          )
        },
        filtered_members_for_billing_key(key) {
          const base = this.filter_snapshots
            ? this.filter_snapshots.afterSetting
            : this.items_for_counts({ pricingGap: true })
          return base.filter(
            item => item.billing_group_key === key && !this.is_billing_group_summary(item),
          )
        },
        pricing_unit_for_filtered_billing_key(key) {
          const members = this.filtered_members_for_billing_key(key)
          if (!members.length)
            return null
          if (members.length > 1)
            return this.build_billing_group_summary(key, members)
          return members[0]
        },
        pricing_gap_for_unit(unit) {
          if (!unit)
            return null

          const stripeLabelKey = this.selected_feature ? this.feature_stripe_label_key(this.selected_feature) : null
          if (this.selected_feature && stripeLabelKey) {
            const packageKey = this.selected_feature_package_key()
            const monthlyBreakdown = this.resolve_monthly_pricing(unit).breakdown
            const monthlyEntry = packageKey
              ? monthlyBreakdown.find(entry => entry.key === packageKey)
              : null
            const stripeBreakdown = this.stripe_pricing_breakdown(unit)
            const stripeEntry = stripeBreakdown.find(
              entry => this.normalize_package_label(entry.label) === stripeLabelKey,
            )
            const monthlyAmount = monthlyEntry ? monthlyEntry.price : null
            const stripeAmount = stripeEntry ? stripeEntry.price : null
            if (stripeAmount == null && (monthlyAmount == null || monthlyAmount === -1))
              return null
            if (stripeAmount != null) {
              if (monthlyAmount != null && monthlyAmount !== -1)
                return stripeAmount - monthlyAmount
              return stripeAmount
            }
            return -monthlyAmount
          }

          return this.row_pricing_diff(unit)
        },
        pricing_gap_context_for_item(item) {
          const key = item.billing_group_key || this.billing_group_key(item)
          if (!key)
            return null

          const map = this.pricing_gap_context_by_key
          if (map && map[key])
            return { key, cached: map[key].cached, unit: map[key].unit }

          if (!this.has_active_filters_excluding_pricing_gap()) {
            const cached = this.pricing_unit_cache[key]
            return cached ? { key, cached, unit: this.pricing_unit_for_billing_key(key) } : null
          }

          const unit = this.pricing_unit_for_filtered_billing_key(key)
          if (!unit)
            return null

          return {
            key,
            unit,
            cached: {
              monthly: this.row_monthly_pricing_total(unit),
              stripe: this.row_stripe_amount(unit),
              gap: this.pricing_gap_for_unit(unit),
              excluded: this.is_pricing_excluded(unit),
              custom: this.is_custom_pricing_unit(unit),
              smsOver: this.tenant_sms_exceeds_alerts_estimate(unit),
            },
          }
        },
        is_billing_group_expanded(key) {
          return this.expanded_billing_groups[key] === true
        },
        toggle_billing_group(key) {
          this.$set(this.expanded_billing_groups, key, !this.is_billing_group_expanded(key))
          this.items = this.build_display_items(this.filtered_tenants)
        },
        build_billing_group_summary(key, members) {
          const stripeMember = this.billing_group_stripe_member(members)
          const summary = {
            _billing_group_summary: true,
            billing_group_key: key,
            tenant_id: '__summary__' + key,
            tenant_name: key + ' — Billing Summary',
            billing_group_size: members.length,
            _summary_members: members,
            vehicle_count: this.billing_group_vehicle_count(members),
            driver_count: this.billing_group_driver_count(members),
            last_actuals_request_time: this.billing_group_latest_field(members, 'last_actuals_request_time'),
            last_booking_update_time: this.billing_group_latest_field(members, 'last_booking_update_time'),
          }
          if (stripeMember) {
            summary._pricing_storage_tenant_id = stripeMember.tenant_id
            summary.stripe_customer_name = stripeMember.stripe_customer_name
            summary.stripe_customer_email = stripeMember.stripe_customer_email
            summary.stripe_subscription_display = stripeMember.stripe_subscription_display
            summary.stripe_subscription_monthly_amount = stripeMember.stripe_subscription_monthly_amount
            summary.stripe_subscription_monthly_cents = stripeMember.stripe_subscription_monthly_cents
            summary.stripe_subscription_status = stripeMember.stripe_subscription_status
            summary.stripe_subscription_id = stripeMember.stripe_subscription_id
            summary.stripe_subscription_cancel_at_period_end = stripeMember.stripe_subscription_cancel_at_period_end
            summary.stripe_subscription_match_method = stripeMember.stripe_subscription_match_method
            summary.stripe_subscription_items = stripeMember.stripe_subscription_items || []
            summary.tenant_manager_pricing_monthly_override = stripeMember.tenant_manager_pricing_monthly_override
            summary.tenant_manager_pricing_excluded = stripeMember.tenant_manager_pricing_excluded
          }
          return summary
        },
        is_billing_group_summary(item) {
          return item._billing_group_summary === true
        },
        is_billing_group_tenant_row(item) {
          return !this.is_billing_group_summary(item) && (item.billing_group_size || 0) > 1
        },
        show_row_pricing(item) {
          if (this.is_billing_group_summary(item)) {
            if (this.selected_feature && !this.feature_stripe_label_key(this.selected_feature))
              return false
            return true
          }
          if ((item.billing_group_size || 0) > 1)
            return false
          if (this.selected_feature && !this.feature_stripe_label_key(this.selected_feature))
            return false
          return true
        },
        show_row_stripe_pricing(item) {
          if (!this.show_row_pricing(item))
            return false
          return this.tenant_has_billable_stripe_match(item)
        },
        tenant_has_billable_stripe_match(item) {
          const unit = this.pricing_billing_unit_for_tenant(item)
          const source = this.stripe_data_item(unit)
          if (!this.is_stripe_subscription_billable(source))
            return false
          return this.stripe_total_amount(source) != null
            || (Array.isArray(source.stripe_subscription_items) && source.stripe_subscription_items.length > 0)
        },
        show_row_pricing_gap(item) {
          if (!this.show_row_pricing(item))
            return false
          if (this.has_monthly_pricing_override(item))
            return true
          if (this.row_monthly_pricing_source(item) === 'stripe')
            return false
          return true
        },
        summary_members(item) {
          if (item._summary_members)
            return item._summary_members
          if (this.has_active_filters_excluding_pricing_gap())
            return this.visible_group_members(item.billing_group_key)
          const allMembers = this.billing_group_members(item.billing_group_key)
          if (allMembers.length)
            return allMembers
          return this.visible_group_members(item.billing_group_key)
        },
        visible_group_members(key) {
          return this.filtered_tenants.filter(item => item.billing_group_key === key)
        },
        row_users(item) {
          if (this.is_billing_group_summary(item))
            return this.tenant_stripe_users(item)

          const admins = this.sorted_customer_admins(item.users)
          const users = []
          if ((item.billing_group_size || 0) <= 1) {
            const adminUsernames = new Set(
              admins
                .map(user => (user.username || '').trim().toLowerCase())
                .filter(username => username && username !== '—'),
            )
            for (const stripeUser of this.tenant_stripe_users(item)) {
              const email = (stripeUser.username || '').trim().toLowerCase()
              if (email && email !== '—' && adminUsernames.has(email))
                continue
              users.push(stripeUser)
            }
          }
          users.push(...admins)
          return users
        },
        row_platform_members(item) {
          if (this.is_billing_group_summary(item))
            return this.summary_members(item)
          return [item]
        },
        row_platform_tenant_types(item) {
          const types = new Set()
          for (const member of this.row_platform_members(item)) {
            if (member.tenant_type)
              types.add(member.tenant_type)
          }
          return Array.from(types).sort()
        },
        row_platform_telematics_types(item) {
          const types = new Set()
          for (const member of this.row_platform_members(item)) {
            if (member.telematics_type)
              types.add(member.telematics_type)
          }
          return Array.from(types).sort()
        },
        row_platform_maint_labels(item) {
          const labels = []
          for (const [key, label] of Object.entries(this.maint_filters)) {
            if (this.row_platform_members(item).some(member => this.tenant_maint_enabled(member, key)))
              labels.push({ key, label })
          }
          return labels
        },
        row_motorcoach_organizations(item) {
          const orgs = new Set()
          for (const member of this.row_platform_members(item)) {
            const raw = member.motorcoach_organizations
            if (!raw)
              continue
            if (Array.isArray(raw)) {
              raw.forEach(org => {
                if (org)
                  orgs.add(org)
              })
              continue
            }
            if (typeof raw === 'string') {
              try {
                const parsed = JSON.parse(raw)
                if (Array.isArray(parsed)) {
                  parsed.forEach(org => {
                    if (org)
                      orgs.add(org)
                  })
                }
              } catch (e) {
                raw.split(',').forEach(org => {
                  const trimmed = org.trim()
                  if (trimmed)
                    orgs.add(trimmed)
                })
              }
            }
          }
          return Array.from(orgs).sort()
        },
        tenant_maint_enabled(item, key) {
          return this.tenant_feature_enabled(item, key)
        },
        row_platforms_sort_label(item) {
          const tenantTypes = this.row_platform_tenant_types(item).join(',')
          const telematicsTypes = this.row_platform_telematics_types(item).join(',')
          const maintTypes = this.row_platform_maint_labels(item).map(entry => entry.label).join(',')
          return tenantTypes + '|' + telematicsTypes + '|' + maintTypes
        },
        row_orgs_sort_label(item) {
          return this.row_motorcoach_organizations(item).join(',')
        },
        row_latest_login_users(item) {
          const users = []
          for (const member of this.row_platform_members(item))
            users.push(...this.sorted_customer_admins(member.users))
          return users
        },
        row_latest_login(item) {
          let bestUser = null
          let bestTime = null
          for (const user of this.row_latest_login_users(item)) {
            const parsed = this.parse_tenant_date(user.last_login_time)
            if (!parsed)
              continue
            if (!bestTime || parsed.isAfter(bestTime)) {
              bestTime = parsed
              bestUser = user
            }
          }
          if (!bestUser)
            return null
          return { user: bestUser, time: bestTime }
        },
        row_latest_login_user_label(item) {
          const login = this.row_latest_login(item)
          if (!login)
            return '—'
          const user = login.user
          const name = ((user.first_name || '') + ' ' + (user.last_name || '')).trim()
          return name || user.username || '—'
        },
        row_latest_login_sort_value(item) {
          const login = this.row_latest_login(item)
          return login ? login.time.valueOf() : 0
        },
        tenant_stripe_users(item) {
          if (!item.stripe_customer_email && !item.stripe_customer_name)
            return []

          return [{
            username: item.stripe_customer_email || '—',
            first_name: item.stripe_customer_name || '—',
            last_name: '',
            job_title: 'Stripe Billing',
            last_login_time: null,
          }]
        },
        user_row_key(user, index) {
          if (user.id)
            return user.id
          const username = user.username || 'user'
          const title = user.job_title || 'admin'
          return username + '|' + title + '|' + index
        },
        row_detail_key(item) {
          return item.tenant_id || item.tenant_name || ''
        },
        is_row_json_visible(item) {
          return this.expanded_row_json[this.row_detail_key(item)] === true
        },
        toggle_row_json(item) {
          const key = this.row_detail_key(item)
          this.$set(this.expanded_row_json, key, !this.expanded_row_json[key])
        },
        billing_group_stripe_users(item) {
          return this.tenant_stripe_users(item)
        },
        row_feature_enabled(item, key) {
          if (this.is_billing_group_summary(item))
            return this.summary_members(item).some(member => this.tenant_feature_flag_enabled(member, key))
          return this.tenant_feature_flag_enabled(item, key)
        },
        billing_group_aggregate_count(members, countFn) {
          if (!members || !members.length)
            return 0
          const counts = members.map(member => countFn(member))
          const first = counts[0]
          if (counts.every(count => count === first))
            return first
          return counts.reduce((sum, count) => sum + count, 0)
        },
        billing_group_vehicle_count(members) {
          return this.billing_group_aggregate_count(members, member => this.tenant_vehicle_count(member))
        },
        billing_group_driver_count(members) {
          return this.billing_group_aggregate_count(members, member => this.tenant_driver_count(member))
        },
        row_vehicle_count(item) {
          if (this.is_billing_group_summary(item))
            return this.billing_group_vehicle_count(this.summary_members(item))
          return this.tenant_vehicle_count(item)
        },
        row_driver_count(item) {
          if (this.is_billing_group_summary(item))
            return this.billing_group_driver_count(this.summary_members(item))
          return this.tenant_driver_count(item)
        },
        row_last_actuals_request_time(item) {
          if (this.is_billing_group_summary(item))
            return this.billing_group_latest_field(this.summary_members(item), 'last_actuals_request_time')
          return item.last_actuals_request_time
        },
        row_last_booking_update_time(item) {
          if (this.is_billing_group_summary(item))
            return this.billing_group_latest_field(this.summary_members(item), 'last_booking_update_time')
          return item.last_booking_update_time
        },
        billing_group_latest_field(members, field) {
          let latestMoment = null
          let latestValue = null
          for (const member of members) {
            const parsed = this.parse_tenant_date(member[field])
            if (!parsed)
              continue
            if (!latestMoment || parsed.isAfter(latestMoment)) {
              latestMoment = parsed
              latestValue = member[field]
            }
          }
          return latestValue
        },
        billing_group_enabled_packages(members) {
          const packageSet = new Set()
          for (const member of members) {
            for (const pkg of this.tenant_enabled_packages(member))
              packageSet.add(pkg)
          }
          return PACKAGE_ORDER.filter(pkg => packageSet.has(pkg))
        },
        billing_group_pricing_breakdown(members) {
          const vehicleCount = this.billing_group_vehicle_count(members)
          const packages = this.billing_group_enabled_packages(members)
          if (!packages.length)
            return []

          return packages.map(packageKey => {
            if (packageKey === 'alerts')
              return this.build_alerts_pricing_entry(null, vehicleCount, this.members_alerts_sms_costs(members))
            return {
              key: packageKey,
              label: PACKAGE_PRICING[packageKey].label,
              price: this.package_price_for_vehicle_count(packageKey, vehicleCount),
            }
          })
        },
        billing_group_monthly_total_for_members(members) {
          if (!members.length)
            return null

          const breakdown = this.billing_group_pricing_breakdown(members)
          if (!breakdown.length)
            return null

          if (breakdown.some(entry => entry.price == null))
            return -1

          return breakdown.reduce((sum, entry) => sum + entry.price, 0)
        },
        website_monthly_pricing_breakdown(item) {
          if (this.is_billing_group_summary(item))
            return this.billing_group_pricing_breakdown(this.summary_members(item))
          return this.tenant_pricing_breakdown(item)
        },
        website_monthly_pricing_total(item) {
          if (this.is_billing_group_summary(item))
            return this.billing_group_monthly_total_for_members(this.summary_members(item))
          return this.tenant_pricing_total(item)
        },
        billing_group_stripe_member(members) {
          if (!members || !members.length)
            return null
          const billable = members.filter(member => this.is_stripe_subscription_billable(member))
          if (!billable.length)
            return null
          return billable.find(member =>
            Array.isArray(member.stripe_subscription_items) && member.stripe_subscription_items.length,
          ) || billable.find(member =>
            this.stripe_total_amount(member) != null,
          ) || billable.find(member =>
            member.stripe_subscription_display,
          ) || null
        },
        is_stripe_subscription_billable(source) {
          if (!source)
            return false
          const status = (source.stripe_subscription_status || '').toLowerCase()
          if (status && NON_BILLABLE_STRIPE_SUBSCRIPTION_STATUSES.has(status))
            return false
          if (source.stripe_subscription_cancel_at_period_end === true
            || source.stripe_subscription_cancel_at_period_end === 'True')
            return false
          if (!status) {
            return false
          }
          return BILLABLE_STRIPE_SUBSCRIPTION_STATUSES.has(status)
        },
        stripe_data_item(item) {
          if (this.is_stripe_subscription_billable(item) && (
            this.stripe_total_amount(item) != null
            || (Array.isArray(item.stripe_subscription_items) && item.stripe_subscription_items.length)
            || item.stripe_subscription_display
          )) {
            return item
          }
          if (this.is_billing_group_summary(item)) {
            const member = this.billing_group_stripe_member(this.summary_members(item))
            return member || item
          }
          if ((item.billing_group_size || 0) > 1) {
            const member = this.billing_group_stripe_member(this.billing_group_members(item.billing_group_key))
            if (member)
              return member
          }
          return item
        },
        parse_stripe_amount(value) {
          if (value == null || value === '')
            return null
          const amount = Number(value)
          return Number.isFinite(amount) ? amount : null
        },
        stripe_total_amount(source) {
          if (!source || !this.is_stripe_subscription_billable(source))
            return null
          const fromAmount = this.parse_stripe_amount(source.stripe_subscription_monthly_amount)
          if (fromAmount != null)
            return fromAmount
          const cents = this.parse_stripe_amount(source.stripe_subscription_monthly_cents)
          if (cents != null)
            return Math.round(cents / 100)
          return null
        },
        stripe_item_amount(entry) {
          const fromMonthly = this.parse_stripe_amount(entry.monthly_amount)
          if (fromMonthly != null)
            return fromMonthly
          const cents = this.parse_stripe_amount(entry.monthly_amount_cents)
          if (cents != null)
            return Math.round(cents / 100)
          return null
        },
        stripe_pricing_breakdown(item) {
          const source = this.stripe_data_item(item)
          if (!this.is_stripe_subscription_billable(source))
            return []
          const items = source.stripe_subscription_items
          if (Array.isArray(items) && items.length) {
            const breakdown = items.map((entry, index) => ({
              key: 'stripe-' + index,
              label: entry.label || entry.product_name || 'Subscription',
              price: this.stripe_item_amount(entry),
            })).filter(entry => entry.price != null)
            if (breakdown.length)
              return breakdown
          }

          const total = this.stripe_total_amount(source)
          if (total != null)
            return [{ key: 'stripe-total', label: 'Subscription', price: total }]
          return []
        },
        stripe_pricing_total(item) {
          const breakdown = this.stripe_pricing_breakdown(item)
          if (!breakdown.length)
            return null
          return breakdown.reduce((sum, entry) => sum + entry.price, 0)
        },
        pricing_storage_tenant(item) {
          if (this.is_billing_group_summary(item)) {
            const members = this.summary_members(item)
            if (item._pricing_storage_tenant_id) {
              const match = members.find(member => member.tenant_id === item._pricing_storage_tenant_id)
              if (match)
                return match
            }
            return this.billing_group_stripe_member(members) || members[0]
          }
          return item
        },
        normalize_monthly_override_value(raw) {
          if (!raw || typeof raw !== 'object')
            return {}
          const packages = {}
          if (raw.packages && typeof raw.packages === 'object') {
            for (const [packageKey, value] of Object.entries(raw.packages)) {
              if (value != null && String(value).trim() !== '')
                packages[packageKey] = String(value).trim()
            }
          }
          const result = { packages }
          return result
        },
        build_monthly_override_payload(override) {
          const packages = { ...(override.packages || {}) }
          for (const packageKey of Object.keys(packages)) {
            if (!String(packages[packageKey]).trim())
              delete packages[packageKey]
          }
          const hasPackages = Object.keys(packages).length > 0
          if (!hasPackages)
            return { tenant_manager_pricing_monthly_override: null }

          return { tenant_manager_pricing_monthly_override: { packages } }
        },
        update_local_tenant_fields(tenantId, payload) {
          const tenant = this.items_full.find(item => item.tenant_id === tenantId)
          if (!tenant)
            return

          for (const [key, value] of Object.entries(payload)) {
            if (value == null || value === '')
              this.$delete(tenant, key)
            else
              this.$set(tenant, key, value)
          }

          const summaryItem = this.items.find(item =>
            this.is_billing_group_summary(item) && item._pricing_storage_tenant_id === tenantId,
          )
          if (summaryItem) {
            for (const [key, value] of Object.entries(payload)) {
              if (value == null || value === '')
                this.$delete(summaryItem, key)
              else
                this.$set(summaryItem, key, value)
            }
          }

          this.refresh_pricing_cache_for_billing_key(tenant.billing_group_key)
          if (this.filter_snapshots)
            this.schedule_toolbar_refresh()
        },
        schedule_toolbar_refresh() {
          if (this.toolbar_refresh_timer) {
            clearTimeout(this.toolbar_refresh_timer)
            this.toolbar_refresh_timer = null
          }
          this.toolbar_refresh_timer = setTimeout(() => {
            this.toolbar_refresh_timer = null
            if (!this.filter_snapshots)
              return
            this.pricing_gap_context_by_key = this.build_pricing_gap_context_by_key(
              this.filter_snapshots.afterSetting,
            )
            this.rebuild_toolbar_state(this.filter_snapshots)
          }, 0)
        },
        // FI-2083 staff enablement. writes the same tenant field the nav gate,
        // the route gate and the api read, through the save path the pricing
        // overrides already use. staff-only because this whole page is; the
        // server-side half of that gate is FI-2082.
        set_feature_flag(item, key, checked) {
          if (!item || this.is_billing_group_summary(item))
            return
          const tenantId = item.tenant_id
          if (!tenantId)
            return

          this.update_local_tenant_fields(tenantId, { [key]: checked ? 'True' : 'False' })
          this.save_feature_flag(tenantId, key, checked)
        },
        async save_feature_flag(tenantId, key, checked) {
          try {
            await update_tenant_config_settings(tenantId, { [key]: checked ? 'True' : 'False' })
          } catch (e) {
            // put the checkbox back: a stuck one reads as enabled when it isn't
            this.update_local_tenant_fields(tenantId, { [key]: checked ? 'False' : 'True' })
            this.$emit('showAlert', 'failed to save features for ' + tenantId, 'danger', 5000)
          }
        },
        async save_pricing_settings(tenantId, payload, options = {}) {
          if (!options.skipLocalUpdate)
            this.update_local_tenant_fields(tenantId, payload)
          try {
            await update_tenant_config_settings(tenantId, payload)
          } catch (e) {
            this.$emit('showAlert', 'failed to save pricing settings for ' + tenantId, 'danger', 5000)
          }
        },
        pricing_override_key(item) {
          return this.pricing_storage_tenant(item).tenant_id
        },
        pricing_unit_for_billing_key(key) {
          const members = this.billing_group_members(key)
          if (!members.length)
            return null
          if (members.length > 1)
            return this.build_billing_group_summary(key, members)
          return members[0]
        },
        schedule_monthly_override_save(item) {
          const tenant = this.pricing_storage_tenant(item)
          if (!tenant || !tenant.tenant_id)
            return

          const tenantId = tenant.tenant_id
          clearTimeout(this.pricing_save_timers[tenantId])
          this.pricing_save_timers[tenantId] = setTimeout(() => {
            const override = this.get_monthly_pricing_override(item)
            this.save_pricing_settings(
              tenantId,
              this.build_monthly_override_payload(override),
              { skipLocalUpdate: true },
            )
          }, 500)
        },
        is_pricing_excluded(item) {
          const tenant = this.pricing_storage_tenant(item)
          return tenant.tenant_manager_pricing_excluded === true
            || tenant.tenant_manager_pricing_excluded === 'True'
        },
        set_pricing_excluded(item, checked) {
          const tenant = this.pricing_storage_tenant(item)
          if (!tenant || !tenant.tenant_id)
            return

          const payload = {
            tenant_manager_pricing_excluded: checked ? 'True' : null,
          }
          this.update_local_tenant_fields(tenant.tenant_id, payload)
          this.save_pricing_settings(tenant.tenant_id, payload, { skipLocalUpdate: true })
        },
        get_monthly_pricing_override(item) {
          const tenant = this.pricing_storage_tenant(item)
          return this.normalize_monthly_override_value(tenant.tenant_manager_pricing_monthly_override)
        },
        has_monthly_pricing_override(item) {
          const override = this.get_monthly_pricing_override(item)
          return Object.values(override.packages || {}).some(value => value != null && String(value).trim() !== '')
        },
        has_monthly_pricing_override_package(item, packageKey) {
          const raw = (this.get_monthly_pricing_override(item).packages || {})[packageKey]
          return raw != null && String(raw).trim() !== ''
        },
        monthly_pricing_override_package(item, packageKey) {
          if (!this.has_monthly_pricing_override_package(item, packageKey))
            return ''
          return String((this.get_monthly_pricing_override(item).packages || {})[packageKey])
        },
        monthly_pricing_override_total_input_value(item) {
          if (!this.has_monthly_pricing_override(item))
            return ''
          const total = this.effective_monthly_pricing(item).total
          if (total == null || total === -1)
            return ''
          return String(total)
        },
        monthly_pricing_total_placeholder(item) {
          const resolved = this.resolve_monthly_pricing(item)
          if (resolved.total == null)
            return ''
          if (resolved.total === -1)
            return 'Custom'
          return String(resolved.total)
        },
        parse_override_amount(value) {
          if (value == null || value === '')
            return null
          const cleaned = String(value).replace(/[^0-9.-]/g, '')
          if (!cleaned)
            return null
          const amount = Number(cleaned)
          return Number.isFinite(amount) ? amount : null
        },
        set_monthly_pricing_override_package(item, packageKey, value) {
          const tenant = this.pricing_storage_tenant(item)
          if (!tenant || !tenant.tenant_id)
            return

          const current = this.get_monthly_pricing_override(item)
          const packages = { ...(current.packages || {}) }
          const trimmed = (value || '').trim()
          if (trimmed)
            packages[packageKey] = trimmed
          else
            delete packages[packageKey]

          const next = { packages }

          const payload = this.build_monthly_override_payload(next)
          this.update_local_tenant_fields(tenant.tenant_id, payload)
          this.schedule_monthly_override_save(item)
        },
        monthly_pricing_package_placeholder(entry) {
          if (entry.price == null)
            return 'Custom'
          return String(entry.price)
        },
        sum_pricing_breakdown_total(breakdown, fallbackTotal) {
          if (!breakdown || !breakdown.length)
            return fallbackTotal
          const priced = breakdown.filter(entry => entry.price != null)
          if (!priced.length)
            return fallbackTotal
          return priced.reduce((sum, entry) => sum + entry.price, 0)
        },
        apply_monthly_pricing_overrides(item, resolved) {
          const override = this.get_monthly_pricing_override(item)
          const packageOverrides = override.packages || {}
          const hasPackageOverride = Object.values(packageOverrides).some(
            value => value != null && String(value).trim() !== '',
          )

          const breakdown = (resolved.breakdown || []).map(entry => {
            const next = { ...entry }
            const raw = packageOverrides[entry.key]
            if (raw != null && String(raw).trim() !== '') {
              const parsed = this.parse_override_amount(raw)
              if (parsed != null)
                next.price = parsed
            }
            return next
          })

          const total = this.sum_pricing_breakdown_total(breakdown, resolved.total)

          if (!hasPackageOverride)
            return { ...resolved, total, breakdown, has_override: false }

          return {
            ...resolved,
            total,
            breakdown,
            source: 'override',
            has_override: true,
          }
        },
        effective_monthly_pricing(item) {
          return this.apply_monthly_pricing_overrides(item, this.resolve_monthly_pricing(item))
        },
        is_website_custom_pricing(item) {
          const breakdown = this.website_monthly_pricing_breakdown(item)
          if (breakdown.some(entry => entry.price == null))
            return true
          const total = this.website_monthly_pricing_total(item)
          if (total === -1)
            return true
          if (this.is_billing_group_summary(item))
            return this.billing_group_vehicle_count(this.summary_members(item)) >= 100
          return this.tenant_vehicle_count(item) >= 100
        },
        resolve_monthly_pricing(item) {
          const websiteTotal = this.website_monthly_pricing_total(item)
          const websiteBreakdown = this.website_monthly_pricing_breakdown(item)
          const isCustomTier = this.is_website_custom_pricing(item)

          if (websiteTotal != null && websiteTotal !== -1)
            return { total: websiteTotal, breakdown: websiteBreakdown, source: 'website' }

          const stripeBreakdown = this.stripe_pricing_breakdown(item)
          const stripeTotal = this.stripe_pricing_total(item)
          if (stripeTotal != null)
            return { total: stripeTotal, breakdown: stripeBreakdown, source: 'stripe' }

          if (isCustomTier)
            return { total: -1, breakdown: websiteBreakdown, source: 'website' }

          return { total: websiteTotal, breakdown: websiteBreakdown, source: 'website' }
        },
        row_monthly_pricing_total(item) {
          if (this.is_billing_group_tenant_row(item))
            return null
          return this.effective_monthly_pricing(item).total
        },
        row_monthly_pricing_breakdown(item) {
          return this.effective_monthly_pricing(item).breakdown
        },
        base_monthly_pricing_breakdown(item) {
          return this.filter_pricing_breakdown_by_feature(this.resolve_monthly_pricing(item).breakdown)
        },
        base_monthly_pricing_display(item) {
          const resolved = this.resolve_monthly_pricing(item)
          if (resolved.total == null)
            return '—'
          if (resolved.total === -1)
            return 'Custom'
          return '$' + resolved.total + '/mo'
        },
        row_stripe_pricing_breakdown(item) {
          return this.filter_stripe_breakdown_by_feature(this.stripe_pricing_breakdown_for_row(item))
        },
        tenant_stripe_label_keys(item) {
          const labelKeys = new Set()
          for (const member of this.row_platform_members(item)) {
            for (const packageKey of this.tenant_enabled_packages(member)) {
              const labelKey = this.package_label_key(packageKey)
              if (labelKey)
                labelKeys.add(labelKey)
            }
            for (const featureKey of Object.keys(FEATURE_STRIPE_LABELS)) {
              if (this.tenant_feature_enabled(member, featureKey)) {
                const labelKey = this.feature_stripe_label_key(featureKey)
                if (labelKey)
                  labelKeys.add(labelKey)
              }
            }
          }
          return labelKeys
        },
        is_stripe_entry_mapped(item, entry) {
          const labelKey = this.normalize_package_label(entry.label)
          return this.tenant_stripe_label_keys(item).has(labelKey)
        },
        stripe_pricing_breakdown_for_row(item) {
          return this.stripe_pricing_breakdown(item).map(entry => ({
            ...entry,
            unmapped: !this.is_stripe_entry_mapped(item, entry),
          }))
        },
        stripe_line_tooltip(item, entry) {
          const lines = [entry.label + ': ' + this.format_package_price(entry.price)]
          if (entry.unmapped)
            lines.push('Not mapped to an enabled feature for this tenant')
          const subscriptionTooltip = this.stripe_subscription_tooltip(item)
          if (subscriptionTooltip)
            lines.push(subscriptionTooltip)
          return lines.join('\n')
        },
        feature_filter_package_key(featureKey) {
          return FEATURE_PACKAGE_MAP[featureKey] || null
        },
        feature_stripe_label_key(featureKey) {
          const packageKey = this.feature_filter_package_key(featureKey)
          if (packageKey)
            return this.package_label_key(packageKey)
          const stripeLabel = FEATURE_STRIPE_LABELS[featureKey]
          if (stripeLabel)
            return this.normalize_package_label(stripeLabel)
          return null
        },
        selected_feature_package_key() {
          if (!this.selected_feature)
            return null
          return this.feature_filter_package_key(this.selected_feature)
        },
        package_label_key(packageKey) {
          if (!packageKey || !PACKAGE_PRICING[packageKey])
            return null
          return this.normalize_package_label(PACKAGE_PRICING[packageKey].label)
        },
        filter_pricing_breakdown_by_feature(breakdown) {
          if (!this.selected_feature)
            return breakdown
          const packageKey = this.selected_feature_package_key()
          if (!packageKey)
            return []
          return breakdown.filter(entry => entry.key === packageKey)
        },
        filter_stripe_breakdown_by_feature(breakdown) {
          if (!this.selected_feature)
            return breakdown
          const labelKey = this.feature_stripe_label_key(this.selected_feature)
          if (!labelKey)
            return []
          return breakdown.filter(entry => this.normalize_package_label(entry.label) === labelKey)
        },
        stripe_amount_for_label_key(billingUnit, labelKey) {
          if (!labelKey)
            return null
          const entry = this.stripe_pricing_breakdown(billingUnit).find(
            item => this.normalize_package_label(item.label) === labelKey,
          )
          return entry ? entry.price : null
        },
        stripe_feature_amount(billingUnit, featureKey) {
          return this.stripe_amount_for_label_key(billingUnit, this.feature_stripe_label_key(featureKey))
        },
        stripe_package_amount(billingUnit, packageKey) {
          return this.stripe_amount_for_label_key(billingUnit, this.package_label_key(packageKey))
        },
        format_stripe_total_thousands(amount) {
          if (amount == null || amount <= 0)
            return ''
          const rounded = Math.round(amount / 1000) * 1000
          if (rounded <= 0)
            return ''
          return ' · $' + (rounded / 1000) + 'K'
        },
        feature_filter_button_title(featureKey) {
          // a derived key is not a tenant field, so show what it actually reads
          const sources = DERIVED_FEATURE_FLAGS[featureKey]
          const heading = sources ? sources.join(' + ') : featureKey
          const lines = [heading, 'Tenants: ' + (this.feature_counts[featureKey] || 0)]
          const amount = this.feature_stripe_totals[featureKey]
          if (amount > 0)
            lines.push('Stripe: $' + amount + '/mo')
          return lines.join('\n')
        },
        filter_diff_breakdown_by_feature(breakdown) {
          if (!this.selected_feature)
            return breakdown
          const packageKey = this.selected_feature_package_key()
          const labelKey = this.feature_stripe_label_key(this.selected_feature)
          if (!labelKey)
            return []
          return breakdown.filter(entry =>
            (packageKey && entry.key === packageKey) || this.normalize_package_label(entry.label) === labelKey,
          )
        },
        format_package_price(price) {
          if (price == null)
            return 'Custom'
          return '$' + price + '/mo'
        },
        row_monthly_pricing_display(item) {
          const resolved = this.effective_monthly_pricing(item)
          if (resolved.total == null)
            return '—'
          if (resolved.total === -1)
            return 'Custom'
          return '$' + resolved.total + '/mo'
        },
        row_monthly_pricing_source(item) {
          return this.effective_monthly_pricing(item).source
        },
        row_stripe_pricing_display(item) {
          const source = this.stripe_data_item(item)
          if (source.stripe_subscription_display)
            return source.stripe_subscription_display
          const amount = this.stripe_total_amount(source)
          if (amount == null)
            return '—'
          return '$' + amount + '/mo'
        },
        row_stripe_customer_name(item) {
          const source = this.stripe_data_item(item)
          if (!this.is_stripe_subscription_billable(source))
            return null
          const name = (source.stripe_customer_name || '').trim()
          return name || null
        },
        row_stripe_subscription_id(item) {
          const source = this.stripe_data_item(item)
          return source.stripe_subscription_id || null
        },
        stripe_subscription_url(item) {
          const subscriptionId = this.row_stripe_subscription_id(item)
          if (!subscriptionId)
            return null
          return 'https://dashboard.stripe.com/subscriptions/' + encodeURIComponent(subscriptionId)
        },
        format_signed_currency(amount) {
          if (amount == null)
            return '—'
          if (amount === 0)
            return '$0'
          const prefix = amount > 0 ? '+' : '-'
          return prefix + '$' + Math.abs(amount)
        },
        pricing_billing_unit_for_tenant(tenant) {
          const key = tenant.billing_group_key || this.billing_group_key(tenant)
          const cached = this.billing_unit_by_key[key]
          if (cached)
            return cached
          if ((tenant.billing_group_size || 0) > 1) {
            const members = this.billing_group_members(key)
            return this.build_billing_group_summary(key, members.length ? members : [tenant])
          }
          return tenant
        },
        monthly_package_tooltip(item, entry) {
          const lines = this.pricing_breakdown_tooltip_lines(entry, item)
          const rowTooltip = this.row_monthly_pricing_tooltip(item)
          if (!rowTooltip)
            return lines.join('\n')
          if (!lines.length)
            return rowTooltip
          return lines.join('\n') + '\n\n' + rowTooltip
        },
        row_monthly_pricing_tooltip(item) {
          if (!this.is_billing_group_summary(item))
            return this.tenant_pricing_tooltip(item)

          const members = this.summary_members(item)
          const vehicleCount = this.billing_group_vehicle_count(members)
          const resolved = this.effective_monthly_pricing(item)
          const lines = [
            'Combined monthly estimate for ' + item.billing_group_key,
            'Total vehicles: ' + vehicleCount,
          ]
          if (resolved.has_override)
            lines.push('Manual monthly overrides applied')
          else if (resolved.source === 'stripe')
            lines.push('Using Stripe subscription package pricing (website tier is Custom)')
          else if (this.is_website_custom_pricing(item))
            lines.push('Using top website tier pricing (100+ vehicles)')
          for (const entry of resolved.breakdown) {
            const priceText = entry.price == null ? 'Custom quote' : '$' + entry.price + '/mo'
            const alertLines = this.alerts_pricing_tooltip_lines(entry, item)
            if (alertLines.length)
              lines.push(...alertLines)
            else
              lines.push(entry.label + ': ' + priceText)
          }
          for (const member of members)
            lines.push(member.tenant_name + ': ' + this.tenant_vehicle_count(member) + ' vehicles')
          lines.push(resolved.has_override
            ? 'Source: manual override'
            : resolved.source === 'stripe'
              ? 'Source: Stripe subscription'
              : 'Source: initekconsulting.com/pricing')
          return lines.join('\n')
        },
        row_stripe_amount(item) {
          if (this.is_billing_group_tenant_row(item))
            return null
          return this.tenant_stripe_amount(item)
        },
        row_pricing_diff(item) {
          if (!this.show_row_pricing_gap(item))
            return null

          const monthly = this.row_monthly_pricing_total(item)
          const stripe = this.row_stripe_amount(item)
          if (monthly == null || monthly === -1 || stripe == null)
            return null
          return stripe - monthly
        },
        row_pricing_diff_display(item) {
          const diff = this.row_pricing_diff(item)
          if (diff == null)
            return '—'
          const prefix = diff > 0 ? '+' : diff < 0 ? '-' : ''
          return prefix + '$' + Math.abs(diff)
        },
        normalize_package_label(label) {
          const norm = (label || '').toLowerCase()
          if (norm.includes('basic'))
            return 'basic'
          if (norm.includes('actuals'))
            return 'actuals'
          if (norm.includes('report') || norm.includes('views'))
            return 'views_reports'
          if (norm.includes('alert') || norm.includes('notification'))
            return 'alerts'
          if (norm.includes('vehicle status') || norm === 'vsm')
            return 'vsm'
          if (norm.includes('map feature'))
            return 'maps'
          if (norm.includes('data access'))
            return 'data'
          return norm.replace(/[^a-z0-9]/g, '')
        },
        row_pricing_diff_breakdown(item) {
          if (!this.show_row_pricing_gap(item))
            return []

          const websiteBreakdown = this.effective_monthly_pricing(item).breakdown
          const stripeBreakdown = this.stripe_pricing_breakdown(item)
          const websiteByLabel = {}
          for (const entry of websiteBreakdown) {
            const labelKey = this.normalize_package_label(entry.label)
            websiteByLabel[labelKey] = entry
          }
          const stripeByLabel = {}
          for (const entry of stripeBreakdown) {
            const labelKey = this.normalize_package_label(entry.label)
            stripeByLabel[labelKey] = entry
          }

          const labelKeys = new Set([...Object.keys(websiteByLabel), ...Object.keys(stripeByLabel)])
          const orderedKeys = []
          for (const packageKey of PACKAGE_ORDER) {
            const labelKey = this.normalize_package_label(PACKAGE_PRICING[packageKey].label)
            if (labelKeys.has(labelKey))
              orderedKeys.push(labelKey)
          }
          for (const labelKey of labelKeys) {
            if (!orderedKeys.includes(labelKey))
              orderedKeys.push(labelKey)
          }

          const diffs = []
          for (const labelKey of orderedKeys) {
            const monthlyEntry = websiteByLabel[labelKey]
            const stripeEntry = stripeByLabel[labelKey]
            const monthlyPrice = monthlyEntry ? monthlyEntry.price : null
            const stripePrice = stripeEntry ? stripeEntry.price : null
            const label = (monthlyEntry || stripeEntry).label

            if (monthlyPrice == null && stripePrice == null)
              continue

            let diff = null
            if (monthlyPrice != null && stripePrice != null)
              diff = stripePrice - monthlyPrice
            else if (monthlyPrice == null && stripePrice != null)
              diff = stripePrice
            else if (monthlyPrice != null && stripePrice == null)
              diff = -monthlyPrice

            if (diff == null || diff === 0)
              continue

            diffs.push({
              key: (monthlyEntry && monthlyEntry.key) || (stripeEntry && stripeEntry.key) || labelKey,
              label,
              diff,
            })
          }
          return this.filter_diff_breakdown_by_feature(diffs)
        },
        pricing_diff_package_classes(diff) {
          if (diff == null)
            return []
          const abs = Math.abs(diff)
          if (abs > 500)
            return ['tenant-pricing-diff-high']
          if (abs > 250)
            return ['tenant-pricing-diff-medium']
          if (abs > 100)
            return ['tenant-pricing-diff-low']
          return ['tenant-pricing-diff-package']
        },
        table_sort_compare(a, b, key, sortDesc) {
          const groupA = a.billing_group_key || a.tenant_id || ''
          const groupB = b.billing_group_key || b.tenant_id || ''

          if (groupA === groupB) {
            const rankA = this.is_billing_group_summary(a) ? 0 : 1
            const rankB = this.is_billing_group_summary(b) ? 0 : 1
            if (rankA !== rankB)
              return rankA - rankB
          }

          const valA = this.table_sort_value(a, key)
          const valB = this.table_sort_value(b, key)

          if (valA == null && valB == null)
            return 0
          if (valA == null)
            return 1
          if (valB == null)
            return -1

          let result = 0
          if (typeof valA === 'number' && typeof valB === 'number')
            result = valA - valB
          else
            result = String(valA).localeCompare(String(valB), undefined, { sensitivity: 'base', numeric: true })

          return sortDesc ? -result : result
        },
        table_sort_value(item, key) {
          const sortItem = this.is_billing_group_summary(item) || (item.billing_group_size || 0) <= 1
            ? item
            : this.build_billing_group_summary(item.billing_group_key, this.visible_group_members(item.billing_group_key))

          if (key === 'monthly_pricing')
            return this.row_monthly_pricing_total(sortItem)
          if (key === 'stripe_subscription')
            return this.row_stripe_amount(sortItem)
          if (key === 'pricing_diff') {
            const diff = this.row_pricing_diff(sortItem)
            return diff == null ? null : Math.abs(diff)
          }
          if (key === 'tenant_name')
            return sortItem.tenant_name || ''
          if (key === 'vehicle_count')
            return this.row_vehicle_count(sortItem)
          if (key === 'driver_count')
            return this.row_driver_count(sortItem)
          if (key === 'last_actuals_request_time')
            return this.tenant_date_sort_value(this.row_last_actuals_request_time(sortItem))
          if (key === 'last_booking_update_time')
            return this.tenant_date_sort_value(this.row_last_booking_update_time(sortItem))
          if (key === 'last_login')
            return this.row_latest_login_sort_value(item)
          if (key === 'platforms')
            return this.row_platforms_sort_label(item)
          return sortItem[key]
        },
        build_billing_group_index() {
          const groups = {}
          for (const item of this.items_full) {
            const key = this.resolve_billing_group_key(item)
            item.billing_group_key = key
            if (!groups[key])
              groups[key] = []
            groups[key].push(item)
          }

          for (const members of Object.values(groups)) {
            members.sort((a, b) => (a.tenant_name || '').localeCompare(b.tenant_name || '', undefined, { sensitivity: 'base' }))
            const size = members.length
            members.forEach((item, index) => {
              item.billing_group_size = size
              item.billing_group_leader = index === 0
            })
          }

          this.billing_group_index = groups
        },
        resolve_billing_group_key(item) {
          const name = (item.tenant_name || '').trim()
          const tenantId = (item.tenant_id || '').trim()
          for (const source of [name, tenantId]) {
            const aaahi = source.match(/^AAAHI-([A-Z]+)/i)
            if (aaahi)
              return 'AAAHI-' + aaahi[1].toUpperCase()
          }
          for (const source of [name, tenantId]) {
            const override = BILLING_GROUP_OVERRIDES[(source || '').trim().toLowerCase()]
            if (override)
              return override
          }
          const namePrefix = billingGroupNamePrefix(name)
          if (namePrefix)
            return namePrefix
          return tenantId || name
        },
        billing_group_key(item) {
          if (item.billing_group_key)
            return item.billing_group_key
          return this.resolve_billing_group_key(item)
        },
        billing_group_members(key) {
          return this.billing_group_index[key] || []
        },
        is_billing_group_leader(item) {
          return item.billing_group_leader === true
        },
        billing_group_member_names(key) {
          return this.billing_group_members(key).map(item => item.tenant_name).join(', ')
        },
        billing_group_row_class(item, type) {
          if (type && type !== 'row')
            return ''
          const classes = []
          if (this.is_billing_group_summary(item))
            classes.push('tenant-billing-group-summary')
          else if (this.is_billing_group_tenant_row(item))
            classes.push('tenant-billing-group-member')
          if (this.show_row_pricing(item) && this.is_pricing_excluded(item))
            classes.push('tenant-pricing-excluded-row')
          return classes.join(' ')
        },
        sort_by_billing_group(items) {
          return items.slice().sort((a, b) => {
            const groupCompare = (a.billing_group_key || '').localeCompare(b.billing_group_key || '', undefined, { sensitivity: 'base' })
            if (groupCompare !== 0)
              return groupCompare
            if (this.is_billing_group_leader(a) !== this.is_billing_group_leader(b))
              return this.is_billing_group_leader(a) ? -1 : 1
            return (a.tenant_name || '').localeCompare(b.tenant_name || '', undefined, { sensitivity: 'base' })
          })
        },
        billing_group_monthly_total(key) {
          const members = this.billing_group_members(key)
          if (!members.length)
            return null
          if (members.length > 1)
            return this.row_monthly_pricing_total(this.build_billing_group_summary(key, members))
          return this.row_monthly_pricing_total(members[0])
        },
        billing_group_stripe_amount(key) {
          const members = this.billing_group_members(key)
          if (!members.length)
            return null
          return this.tenant_stripe_amount(members[0])
        },
        billing_group_pricing_diff(key) {
          const members = this.billing_group_members(key)
          if (!members.length)
            return null

          const unit = members.length > 1
            ? this.build_billing_group_summary(key, members)
            : members[0]
          if (!this.show_row_pricing_gap(unit))
            return null

          const monthly = this.row_monthly_pricing_total(unit)
          const stripe = this.row_stripe_amount(unit)
          if (monthly == null || monthly === -1 || stripe == null)
            return null
          return stripe - monthly
        },
        filter_results() {
          this.apply_filters(false)
        },
        tenant_feature_flag_enabled(item, key) {
          if (key === 'basic_enabled')
            return this.tenant_has_basic_package(item)
          const sources = DERIVED_FEATURE_FLAGS[key]
          if (sources)
            return sources.some(flag => this.tenant_feature_flag_enabled(item, flag))
          const value = item[key]
          return value === true || value === 'True'
        },
        tenant_has_basic_package(item) {
          const hasPaidPackage = this.tenant_feature_enabled(item, 'actuals_enabled')
            || this.tenant_feature_enabled(item, 'data_services_enabled')
            || this.tenant_feature_enabled(item, 'views_reports_enabled')
            || this.tenant_has_alerts_package(item)
            || this.tenant_feature_enabled(item, 'vehicle_status_management_enabled')

          return hasPaidPackage
            || (item.telematics_type && item.telematics_type !== 'NONE')
            || this.tenant_feature_enabled(item, 'routes_enabled')
        },
        build_tenant_name_username_blob(item) {
          const parts = [
            item.tenant_name,
            item.tenant_id,
          ]
          for (const user of (item.users || [])) {
            if (user.username)
              parts.push(user.username)
            if (user.first_name)
              parts.push(user.first_name)
            if (user.last_name)
              parts.push(user.last_name)
          }
          return parts.filter(Boolean).join(' ').toLowerCase()
        },
        build_tenant_search_indexes() {
          for (const item of this.items_full)
            item._name_username_blob = this.build_tenant_name_username_blob(item)
        },
        build_enabled_setting_options() {
          const keys = new Set()
          const collect = (obj) => {
            if (obj == null || typeof obj !== 'object')
              return
            if (Array.isArray(obj)) {
              obj.forEach(collect)
              return
            }
            for (const [key, value] of Object.entries(obj)) {
              if (key.endsWith('_enabled'))
                keys.add(key)
              if (value != null && typeof value === 'object')
                collect(value)
            }
          }
          this.items_full.forEach(item => collect(item))
          const sorted = Array.from(keys).sort((a, b) => a.localeCompare(b))
          return [{ value: null, text: '-- setting --' }].concat(
            sorted.map(k => ({ value: k, text: k }))
          )
        },
        is_custom_pricing_unit(unit) {
          if (!unit)
            return false
          if (this.is_website_custom_pricing(unit))
            return true
          return this.resolve_monthly_pricing(unit).source === 'stripe'
        },
        refresh_pricing_cache_for_billing_key(key) {
          const unit = this.pricing_unit_for_billing_key(key)
          if (!unit) {
            this.$delete(this.pricing_unit_cache, key)
            this.$delete(this.billing_unit_by_key, key)
            return
          }
          this.$set(this.billing_unit_by_key, key, unit)
          this.$set(this.pricing_unit_cache, key, {
            monthly: this.row_monthly_pricing_total(unit),
            stripe: this.row_stripe_amount(unit),
            gap: this.row_pricing_diff(unit),
            excluded: this.is_pricing_excluded(unit),
            custom: this.is_custom_pricing_unit(unit),
            smsOver: this.tenant_sms_exceeds_alerts_estimate(unit),
          })
        },
        refresh_all_pricing_caches() {
          const cache = {}
          const seen = new Set()
          for (const item of this.items_full) {
            const key = item.billing_group_key
            if (seen.has(key))
              continue
            seen.add(key)
            const unit = this.pricing_unit_for_billing_key(key)
            if (!unit)
              continue
            cache[key] = {
              monthly: this.row_monthly_pricing_total(unit),
              stripe: this.row_stripe_amount(unit),
              gap: this.row_pricing_diff(unit),
              excluded: this.is_pricing_excluded(unit),
              custom: this.is_custom_pricing_unit(unit),
              smsOver: this.tenant_sms_exceeds_alerts_estimate(unit),
            }
          }
          this.pricing_unit_cache = cache
          this.rebuild_billing_unit_index()
        },
        format_last_login(value) {
          if (value == null || value === '' || value === 0 || value === '0')
            return '—'

          if (typeof value === 'string' && value.indexOf('T') !== -1) {
            const m = moment(value)
            return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : '—'
          }

          const n = Number(value)
          if (!Number.isNaN(n) && n > 0) {
            const m = n < 1e12 ? moment.unix(n) : moment(n)
            return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : '—'
          }

          const m = moment(String(value))
          return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : '—'
        },
        escape_csv_value(value) {
          if (value == null)
            return ''
          const str = String(value)
          if (str === '—' || str === 'Invalid date')
            return ''
          if (/[",\r\n]/.test(str))
            return '"' + str.replace(/"/g, '""') + '"'
          return str
        },
        csv_blank_if_empty(value) {
          if (value == null || value === '' || value === '—' || value === 'Invalid date')
            return ''
          return value
        },
        csv_format_tenant_date(value) {
          const parsed = this.parse_tenant_date(value)
          return parsed ? parsed.format('MM/DD/YY') : ''
        },
        csv_format_last_login(value) {
          if (value == null || value === '' || value === 0 || value === '0')
            return ''

          if (typeof value === 'string' && value.indexOf('T') !== -1) {
            const m = moment(value)
            return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : ''
          }

          const n = Number(value)
          if (!Number.isNaN(n) && n > 0) {
            const m = n < 1e12 ? moment.unix(n) : moment(n)
            return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : ''
          }

          const m = moment(String(value))
          return m.isValid() ? m.format('MM/DD/YY hh:mm:ss A') : ''
        },
        csv_export_platforms_label(item) {
          const parts = [
            ...this.row_platform_tenant_types(item),
            ...this.row_platform_telematics_types(item),
            ...this.row_platform_maint_labels(item).map(entry => entry.label),
          ]
          return parts.length ? parts.join(', ') : ''
        },
        csv_export_features_label(item) {
          const labels = []
          for (const [key, label] of Object.entries(this.feature_filters)) {
            if (this.row_feature_enabled(item, key))
              labels.push(label)
          }
          return labels.length ? labels.join(', ') : ''
        },
        csv_export_cell_value(item, fieldKey) {
          switch (fieldKey) {
            case 'tenant_name':
              if (this.is_billing_group_summary(item))
                return item.tenant_name + ' (' + item.billing_group_size + ' tenants)'
              return item.tenant_name || ''
            case 'platforms':
              return this.csv_export_platforms_label(item)
            case 'orgs': {
              const orgs = this.row_motorcoach_organizations(item)
              return orgs.length ? orgs.join(', ') : ''
            }
            case 'last_login': {
              const login = this.row_latest_login(item)
              if (!login)
                return ''
              const time = this.csv_format_last_login(login.user.last_login_time)
              const userLabel = this.csv_blank_if_empty(this.row_latest_login_user_label(item))
              if (!time && !userLabel)
                return ''
              if (time && userLabel)
                return time + ' | ' + userLabel
              return time || userLabel
            }
            case 'last_actuals_request_time':
              return this.csv_format_tenant_date(this.row_last_actuals_request_time(item))
            case 'last_booking_update_time':
              return this.csv_format_tenant_date(this.row_last_booking_update_time(item))
            case 'vehicle_count':
              return this.row_vehicle_count(item)
            case 'driver_count':
              return this.row_driver_count(item)
            case 'monthly_pricing':
              return this.csv_blank_if_empty(this.show_row_pricing(item) ? this.row_monthly_pricing_display(item) : '')
            case 'stripe_subscription':
              return this.csv_blank_if_empty(this.show_row_stripe_pricing(item) ? this.row_stripe_pricing_display(item) : '')
            case 'pricing_diff':
              return this.csv_blank_if_empty(this.show_row_pricing_gap(item) ? this.row_pricing_diff_display(item) : '')
            case 'features':
              return this.csv_export_features_label(item)
            default:
              return this.csv_blank_if_empty(item[fieldKey])
          }
        },
        download_filtered_csv() {
          const rows = this.items.filter(item => !this.is_billing_group_tenant_row(item))
          const columns = this.visible_fields.filter(field => field.key !== 'details')
          const lines = [
            columns.map(field => this.escape_csv_value(field.label || field.key)).join(','),
          ]
          for (const item of rows) {
            lines.push(columns.map(field => this.escape_csv_value(this.csv_export_cell_value(item, field.key))).join(','))
          }
          const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = 'tenant-manager-' + moment().format('YYYY-MM-DD') + '.csv'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        },
        tenant_feature_enabled(item, key) {
          return this.tenant_has_key_value(item, key, 'True')
        },
        tenant_vehicle_count(item) {
          const count = Number(item.vehicle_count)
          return Number.isFinite(count) && count >= 0 ? count : 0
        },
        tenant_driver_count(item) {
          const count = Number(item.driver_count)
          return Number.isFinite(count) && count >= 0 ? count : 0
        },
        format_tenant_date(value) {
          const parsed = this.parse_tenant_date(value)
          return parsed ? parsed.format('MM/DD/YY') : '—'
        },
        parse_tenant_date(value) {
          if (value == null || value === '' || value === 0 || value === '0')
            return null

          if (typeof value === 'string' && value.indexOf('T') !== -1) {
            const m = moment(value)
            return m.isValid() ? m : null
          }

          const n = Number(value)
          if (!Number.isNaN(n) && n > 0) {
            const m = n < 1e12 ? moment.unix(n) : moment(n)
            return m.isValid() ? m : null
          }

          const m = moment(String(value))
          return m.isValid() ? m : null
        },
        tenant_date_sort_value(value) {
          const parsed = this.parse_tenant_date(value)
          return parsed ? parsed.valueOf() : 0
        },
        package_price_for_vehicle_count(packageKey, vehicleCount) {
          const packageDef = PACKAGE_PRICING[packageKey]
          if (!packageDef)
            return null
          if (packageDef.defaultPrice != null)
            return packageDef.defaultPrice

          const tiers = packageDef.tiers || []
          if (!tiers.length)
            return null
          if (vehicleCount >= 100)
            return tiers[tiers.length - 1].price

          for (const tier of tiers) {
            if (vehicleCount <= tier.maxVehicles)
              return tier.price
          }
          return tiers[tiers.length - 1].price
        },
        tenant_has_alerts_package(item) {
          return this.tenant_feature_enabled(item, 'alerts_enabled')
        },
        tenant_enabled_packages(item) {
          const packages = []

          if (this.tenant_has_basic_package(item))
            packages.push('basic')
          if (this.tenant_feature_enabled(item, 'actuals_enabled'))
            packages.push('actuals')
          if (this.tenant_feature_enabled(item, 'views_reports_enabled'))
            packages.push('views_reports')
          if (this.tenant_has_alerts_package(item))
            packages.push('alerts')
          if (this.tenant_feature_enabled(item, 'vehicle_status_management_enabled'))
            packages.push('vsm')
          if (this.tenant_feature_enabled(item, 'data_services_enabled'))
            packages.push('data')
          if (this.tenant_feature_enabled(item, 'maps_enabled'))
            packages.push('maps')

          return packages
        },
        tenant_pricing_breakdown(item) {
          const vehicleCount = this.tenant_vehicle_count(item)
          const packages = this.tenant_enabled_packages(item)
          if (!packages.length)
            return []

          return packages.map(packageKey => this.build_pricing_breakdown_entry(packageKey, item, vehicleCount))
        },
        build_pricing_breakdown_entry(packageKey, item, vehicleCount) {
          if (packageKey === 'alerts')
            return this.build_alerts_pricing_entry(item, vehicleCount)

          return {
            key: packageKey,
            label: PACKAGE_PRICING[packageKey].label,
            price: this.package_price_for_vehicle_count(packageKey, vehicleCount),
          }
        },
        parse_sms_cost(value) {
          const amount = Number(value)
          return Number.isFinite(amount) ? amount : 0
        },
        tenant_alerts_sms_sent_cost(item) {
          return this.parse_sms_cost(item && item.alerts_sms_sent_cost_30d)
        },
        tenant_alerts_sms_received_cost(item) {
          return this.parse_sms_cost(item && item.alerts_sms_received_cost_30d)
        },
        tenant_alerts_sms_total_cost(item) {
          return this.parse_sms_cost(item && item.alerts_sms_total_cost_30d)
        },
        members_alerts_sms_costs(members) {
          let sent = 0
          let received = 0
          for (const member of members || []) {
            sent += this.tenant_alerts_sms_sent_cost(member)
            received += this.tenant_alerts_sms_received_cost(member)
          }
          return {
            sent,
            received,
            total: sent + received,
          }
        },
        round_to_nearest_25(amount) {
          const value = Number(amount)
          if (!Number.isFinite(value) || value <= 0)
            return 0
          return Math.round(value / 25) * 25
        },
        alerts_sms_actuals_available(item, smsCosts) {
          if (smsCosts != null)
            return true
          if (!item)
            return false
          return (
            item.alerts_sms_total_cost_30d != null
            || item.alerts_sms_sent_cost_30d != null
            || item.alerts_sms_received_cost_30d != null
          )
        },
        alerts_price_from_sms_and_tier(tierPrice, smsTotalCost, smsActualsAvailable) {
          if (!smsActualsAvailable)
            return tierPrice == null ? null : tierPrice

          const smsRounded = this.round_to_nearest_25(smsTotalCost)
          if (tierPrice == null)
            return smsRounded || null
          return Math.max(tierPrice, smsRounded)
        },
        build_alerts_pricing_entry(item, vehicleCount, smsCosts) {
          const tierPrice = this.package_price_for_vehicle_count('alerts', vehicleCount)
          const smsSent = smsCosts ? smsCosts.sent : this.tenant_alerts_sms_sent_cost(item)
          const smsReceived = smsCosts ? smsCosts.received : this.tenant_alerts_sms_received_cost(item)
          const smsTotal = smsCosts ? smsCosts.total : (smsSent + smsReceived)
          const smsActualsAvailable = this.alerts_sms_actuals_available(item, smsCosts)
          const smsRounded = smsActualsAvailable ? this.round_to_nearest_25(smsTotal) : 0
          return {
            key: 'alerts',
            label: PACKAGE_PRICING.alerts.label,
            price: this.alerts_price_from_sms_and_tier(tierPrice, smsTotal, smsActualsAvailable),
            tierPrice,
            smsSentCost: smsSent,
            smsReceivedCost: smsReceived,
            smsTotalCost: smsTotal,
            smsFloorPrice: smsRounded,
            smsActualsAvailable,
          }
        },
        format_sms_cost(amount) {
          return this.parse_sms_cost(amount).toFixed(2)
        },
        format_monthly_package_display(entry) {
          const base = entry.label + ' ' + this.format_package_price(entry.price)
          if (entry.key !== 'alerts')
            return base
          const smsTotal = this.parse_sms_cost(entry.smsTotalCost)
          if (smsTotal <= 0)
            return base
          return base + ' · SMS 30d $' + this.format_sms_cost(smsTotal)
        },
        base_alerts_pricing_entry(item) {
          const unit = this.pricing_billing_unit_for_tenant(item) || item
          if (!unit)
            return null
          return (this.resolve_monthly_pricing(unit).breakdown || []).find(entry => entry.key === 'alerts') || null
        },
        alerts_original_pricing_estimate(entry) {
          if (!entry || entry.key !== 'alerts')
            return null
          return entry.tierPrice
        },
        tenant_sms_exceeds_alerts_estimate(item) {
          const entry = this.base_alerts_pricing_entry(item)
          if (!entry)
            return false
          return this.alerts_sms_exceeds_estimate(item, entry)
        },
        alerts_sms_exceeds_estimate(item, entry) {
          if (!entry || entry.key !== 'alerts')
            return false
          const sms = this.parse_sms_cost(entry.smsTotalCost)
          const originalEstimate = this.alerts_original_pricing_estimate(entry)
          if (sms <= 0 || originalEstimate == null)
            return false
          return sms > originalEstimate
        },
        effective_alerts_pricing_entry(item) {
          const unit = this.pricing_billing_unit_for_tenant(item) || item
          if (!unit)
            return null
          return (this.effective_monthly_pricing(unit).breakdown || []).find(entry => entry.key === 'alerts') || null
        },
        alerts_pricing_tooltip_lines(entry, item) {
          if (!entry || entry.key !== 'alerts')
            return []

          const lines = []
          const tierPrice = entry.tierPrice
          const smsTotal = this.parse_sms_cost(entry.smsTotalCost)
          if (smsTotal > 0) {
            lines.push('SMS past 30 days total: $' + this.format_sms_cost(smsTotal))
            lines.push(
              '  sent $' + this.format_sms_cost(entry.smsSentCost)
                + ', received $' + this.format_sms_cost(entry.smsReceivedCost),
            )
            if (entry.smsActualsAvailable)
              lines.push('SMS estimate (rounded to nearest $25): $' + entry.smsFloorPrice + '/mo')
          } else {
            lines.push('SMS past 30 days: $0.00')
          }
          if (tierPrice != null)
            lines.push('Original pricing estimate (vehicle tier): $' + tierPrice + '/mo')
          const effectiveEntry = item ? this.effective_alerts_pricing_entry(item) : entry
          const adjusted = effectiveEntry ? effectiveEntry.price : entry.price
          if (adjusted != null && adjusted !== tierPrice)
            lines.push('Adjusted alerts price: max(tier, SMS rounded) = $' + adjusted + '/mo')
          else if (adjusted != null && tierPrice == null)
            lines.push('Estimated alerts price: $' + adjusted + '/mo')
          if (item && this.tenant_sms_exceeds_alerts_estimate(item))
            lines.push('Warning: actual SMS cost exceeds original pricing estimate')
          return lines
        },
        pricing_breakdown_tooltip_lines(entry, item) {
          const lines = this.alerts_pricing_tooltip_lines(entry, item)
          if (lines.length)
            return lines
          const priceText = entry.price == null ? 'Custom quote' : '$' + entry.price + '/mo'
          return [entry.label + ': ' + priceText]
        },
        tenant_pricing_total(item) {
          const breakdown = this.tenant_pricing_breakdown(item)
          if (!breakdown.length)
            return null

          if (breakdown.some(entry => entry.price == null))
            return -1

          return breakdown.reduce((sum, entry) => sum + entry.price, 0)
        },
        tenant_pricing_display(item) {
          const total = this.tenant_pricing_total(item)
          if (total == null)
            return '—'
          if (total === -1)
            return 'Custom'
          return '$' + total + '/mo'
        },
        tenant_pricing_tooltip(item) {
          const resolved = this.effective_monthly_pricing(item)
          if (!resolved.breakdown.length)
            return ''

          const vehicleCount = this.tenant_vehicle_count(item)
          const lines = resolved.breakdown.flatMap(entry => {
            const priceText = entry.price == null ? 'Custom quote' : '$' + entry.price + '/mo'
            const alertLines = this.alerts_pricing_tooltip_lines(entry, item)
            if (alertLines.length)
              return alertLines
            return [entry.label + ': ' + priceText]
          })

          lines.unshift('Vehicle count: ' + vehicleCount)
          if (resolved.has_override)
            lines.push('Manual monthly overrides applied')
          else if (resolved.source === 'stripe')
            lines.push('Using Stripe subscription package pricing (website tier is Custom)')
          else if (this.is_website_custom_pricing(item))
            lines.push('Using top website tier pricing (100+ vehicles)')
          lines.push(resolved.has_override
            ? 'Source: manual override'
            : resolved.source === 'stripe'
              ? 'Source: Stripe subscription'
              : 'Source: initekconsulting.com/pricing')
          return lines.join('\n')
        },
        stripe_subscription_tooltip(item) {
          if (!item.stripe_subscription_display && this.row_stripe_amount(item) == null)
            return ''

          const lines = [
            'Stripe customer: ' + (item.stripe_customer_name || '—'),
            'Email: ' + (item.stripe_customer_email || '—'),
            'Status: ' + (item.stripe_subscription_status || '—'),
            'Subscription: ' + (item.stripe_subscription_id || '—'),
            'Billed total: ' + this.row_stripe_pricing_display(item),
          ]
          if (item.stripe_subscription_match_method)
            lines.push('Matched by: ' + item.stripe_subscription_match_method)
          if (this.stripe_pricing_breakdown(item).length)
            lines.push('Package lines from Stripe subscription items')
          if (this.is_billing_group_summary(item)) {
            lines.push('Billing group: ' + item.billing_group_key + ' (' + item.billing_group_size + ' tenants)')
            lines.push('Tenants: ' + this.summary_members(item).map(member => member.tenant_name).join(', '))
          }
          return lines.join('\n')
        },
        tenant_stripe_amount(item) {
          return this.stripe_total_amount(this.stripe_data_item(item))
        },
        tenant_pricing_diff(item) {
          if ((item.billing_group_size || 0) > 1)
            return this.billing_group_pricing_diff(item.billing_group_key)

          return this.row_pricing_diff(item)
        },
        pricing_gap_range_for_key(rangeKey) {
          return this.pricing_gap_ranges.find(range => range.key === rangeKey) || null
        },
        pricing_gap_abs_in_range(abs, range) {
          if (range.max == null)
            return abs > range.min
          return abs >= range.min && abs <= range.max
        },
        pricing_gap_in_selected_range(item, rangeKey) {
          return this.pricing_gap_matches_range(item, rangeKey)
        },
        pricing_gap_matches_range(item, rangeKey) {
          const range = this.pricing_gap_range_for_key(rangeKey)
          if (!range)
            return false
          const ctx = this.pricing_gap_context_for_item(item)
          if (!ctx)
            return false
          return this.pricing_gap_matches_ctx(ctx, range)
        },
        pricing_gap_matches_ctx(ctx, range) {
          const cached = ctx.cached
          const unit = ctx.unit

          if (range.skip)
            return cached.excluded === true

          if (range.custom)
            return cached.custom === true

          if (range.noStripe)
            return cached.excluded !== true && !this.tenant_has_billable_stripe_match(unit)

          if (range.smsOver)
            return cached.smsOver === true

          if (range.nonZeroGap)
            return cached.excluded !== true && cached.gap != null && cached.gap !== 0

          if (cached.excluded || cached.gap == null)
            return false
          return this.pricing_gap_abs_in_range(Math.abs(cached.gap), range)
        },
        rebuild_billing_unit_index() {
          const index = {}
          const seen = new Set()
          for (const item of this.items_full) {
            const key = item.billing_group_key
            if (!key || seen.has(key))
              continue
            seen.add(key)
            index[key] = this.pricing_unit_for_billing_key(key)
          }
          this.billing_unit_by_key = index
        },
        tenant_pricing_diff_display(item) {
          return this.row_pricing_diff_display(item)
        },
        pricing_diff_tooltip(item) {
          const diff = this.row_pricing_diff(item)
          if (diff == null)
            return ''

          const packageDiffs = this.row_pricing_diff_breakdown(item)
          const lines = []

          if (this.is_billing_group_summary(item)) {
            const members = this.summary_members(item)
            lines.push(
              'Billing group: ' + item.billing_group_key + ' (' + item.billing_group_size + ' tenants)',
              'Total vehicles: ' + this.billing_group_vehicle_count(members),
              'Combined monthly estimate: ' + this.row_monthly_pricing_display(item),
              'Stripe: ' + (item.stripe_subscription_display || '—'),
              'Gap (Stripe - Combined Monthly): ' + this.row_pricing_diff_display(item),
            )
          } else {
            lines.push(
              'Monthly estimate: ' + this.row_monthly_pricing_display(item),
              'Stripe: ' + this.row_stripe_pricing_display(item),
              'Gap (Stripe - Monthly): ' + this.row_pricing_diff_display(item),
            )
          }

          if (packageDiffs.length) {
            lines.push('Per-package gaps (Stripe - Monthly estimate):')
            for (const entry of packageDiffs)
              lines.push('  ' + entry.label + ': ' + this.format_signed_currency(entry.diff))
          }

          return lines.join('\n')
        },
        pricing_diff_classes(item) {
          const diff = this.row_pricing_diff(item)
          if (diff == null)
            return []
          const abs = Math.abs(diff)
          if (abs > 500)
            return ['tenant-pricing-diff-high']
          if (abs > 250)
            return ['tenant-pricing-diff-medium']
          if (abs > 100)
            return ['tenant-pricing-diff-low']
          return []
        },
        sorted_customer_admins(users) {
          if (!users)
            return []
          return users
            .filter(u => u.role == 'customer-admin')
            .slice()
            .sort((a, b) => {
              const byFirst = (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' })
              if (byFirst !== 0)
                return byFirst
              return (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' })
            })
        },
        tenant_has_key(obj, searchKey) {
          if (obj == null || typeof obj !== 'object')
            return false

          const normalizedKey = searchKey.toLowerCase()

          if (Array.isArray(obj))
            return obj.some(el => this.tenant_has_key(el, searchKey))

          for (const [key, value] of Object.entries(obj)) {
            if (key.toLowerCase() === normalizedKey)
              return true
            if (value != null && typeof value === 'object' && this.tenant_has_key(value, searchKey))
              return true
          }
          return false
        },
        tenant_has_key_value(obj, searchKey, searchValue) {
          if (obj == null || typeof obj !== 'object')
            return false

          const normalizedKey = searchKey.toLowerCase()

          if (Array.isArray(obj))
            return obj.some(el => this.tenant_has_key_value(el, searchKey, searchValue))

          for (const [key, value] of Object.entries(obj)) {
            if (key.toLowerCase() === normalizedKey && this.values_match(value, searchValue))
              return true
            if (value != null && typeof value === 'object' && this.tenant_has_key_value(value, searchKey, searchValue))
              return true
          }
          return false
        },
        values_match(actual, expected) {
          if (actual == null)
            return expected === '' || expected.toLowerCase() === 'null' || expected.toLowerCase() === 'none'

          const expectedNorm = expected.toLowerCase()
          if (expectedNorm === 'true' || expectedNorm === 'false') {
            const expectedBool = expectedNorm === 'true'
            if (typeof actual === 'boolean')
              return actual === expectedBool
            if (typeof actual === 'string')
              return actual.toLowerCase() === expectedNorm
          }

          return String(actual).toLowerCase() === expectedNorm
        },
        contains(val1, val2) {
          if (val1 != null) {
            return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
          }
          return false;
        },
        // list_contains(records, field, val2) {
        //   for (let record in records) {
        //     console.log('user:' + record)
        //     if (record[field] != null) {
        //       return record[field].toLowerCase().indexOf(val2.toLowerCase()) != -1;
        //     }
        //   }
        //   return false;
        // }
      } ,
      computed:{
        ...mapState({
          user: state => state.profile.user
        }),
        filtered_tenant_count() {
          return this.filtered_tenants.length
        },
        billing_group_cnt() {
          return this.toolbar_state.billing_group_cnt
        },
        pricing_gap_ranges() {
          return PRICING_GAP_DOLLAR_RANGES.concat(PRICING_GAP_SPECIAL_RANGES)
        },
        pricing_gap_range_filters() {
          return this.pricing_gap_ranges.filter(
            range => !range.custom && !range.skip && !range.noStripe && !range.smsOver && !range.nonZeroGap,
          )
        },
        pricing_special_filters() {
          return this.pricing_gap_ranges.filter(
            range => (range.custom || range.noStripe || range.smsOver || range.skip) && !range.nonZeroGap,
          )
        },
        vehicle_cnt() {
          return this.toolbar_state.vehicle_cnt
        },
        driver_cnt() {
          return this.toolbar_state.driver_cnt
        },
        fasttrak_cnt() {
          return this.toolbar_state.fasttrak_cnt
        },
        cm_cnt() {
          return this.toolbar_state.cm_cnt
        },
        tbn_cnt() {
          return this.toolbar_state.tbn_cnt
        },
        vehicle_tier_counts() {
          return this.toolbar_state.vehicle_tier_counts
        },
        samsara_cnt() {
          return this.toolbar_state.samsara_cnt
        },
        saucon_cnt() {
          return this.toolbar_state.saucon_cnt
        },
        zonar_cnt() {
          return this.toolbar_state.zonar_cnt
        },
        motive_cnt() {
          return this.toolbar_state.motive_cnt
        },
        none_cnt() {
          return this.toolbar_state.none_cnt
        },
        maint_counts() {
          return this.toolbar_state.maint_counts
        },
        motorcoach_org_counts() {
          return this.toolbar_state.motorcoach_org_counts
        },
        setting_filter_value_options() {
          return [{ value: null, text: '-- value --' }].concat(this.boolean_options)
        },
        feature_counts() {
          return this.toolbar_state.feature_counts
        },
        feature_stripe_totals() {
          return this.toolbar_state.feature_stripe_totals
        },
        feature_stripe_total_labels() {
          return this.toolbar_state.feature_stripe_total_labels
        },
        pricing_gap_counts() {
          return this.toolbar_state.pricing_gap_counts
        },
        pricing_totals() {
          return this.toolbar_state.pricing_totals
        },
        visible_fields() {
          if (this.billing_loaded)
            return this.fields
          return this.fields.filter(field => !BILLING_TABLE_FIELD_KEYS.has(field.key))
        },
      },
      watch: {
        setting_filter_key() {
          this.setting_filter_value = null
          this.apply_filters(true)
        },
        setting_filter_value() {
          this.apply_filters(true)
        },
        pricing_gap_ranges: {
          handler(ranges) {
            if (this.selected_pricing_gap && !ranges.some(range => range.key === this.selected_pricing_gap))
              this.selected_pricing_gap = null
          },
          deep: true,
        },
      },
      beforeDestroy() {
        if (this.apply_filters_timer)
          clearTimeout(this.apply_filters_timer)
        if (this.filter_work_timer)
          clearTimeout(this.filter_work_timer)
        if (this.toolbar_refresh_timer) {
          if (typeof window !== 'undefined' && window.cancelIdleCallback)
            window.cancelIdleCallback(this.toolbar_rebuild_timer)
          else
            clearTimeout(this.toolbar_rebuild_timer)
        }
        if (this.tenant_search_debounce_timer)
          clearTimeout(this.tenant_search_debounce_timer)
      },
    }
</script>
<style scoped>
.tenant-reload-btn {
  flex: 0 0 auto;
}
.tenant-toolbar {
  margin: 20px;
}
.tenant-filters-row,
.tenant-counts-filters-row,
.tenant-feature-filters-row,
.tenant-search-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px 6px;
  font-weight: bold;
  margin-bottom: 20px;
}
.tenant-filters-row {
  font-size: 13px;
}
.tenant-counts-filters-row,
.tenant-feature-filters-row {
  font-size: 13px;
}
.tenant-counts-filters-row .tenant-filter-label,
.tenant-feature-filters-row .tenant-filter-label {
  font-size: 14px;
}
.tenant-search-row {
  font-size: 13px;
}
.tenant-filter-label {
  margin-right: 4px;
  white-space: nowrap;
}
.tenant-filter-chip {
  display: inline-block;
  border: 1px solid #ced4da;
  background-color: #f8f9fa;
  color: #212529;
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
  line-height: 1.2;
  font-weight: bold;
  font-size: 13px;
}
.tenant-chip {
  display: inline-block;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
  line-height: 1.2;
  font-weight: bold;
  font-size: 13px;
}
.tenant-chip-feature {
  background-color: rgba(40, 167, 69, 0.85);
  border-color: rgba(40, 167, 69, 0.85);
  color: #ffffff;
}
.tenant-chip-billing-group {
  background-color: rgba(232, 223, 245, 0.85);
  border-color: rgba(179, 157, 219, 0.85);
  color: #4a148c;
}
.tenant-chip-pricing-gap {
  background-color: rgba(184, 230, 184, 0.85);
  border-color: rgba(125, 204, 125, 0.85);
  color: #155724;
}
.tenant-chip-pricing-special {
  background-color: rgba(212, 228, 247, 0.85);
  border-color: rgba(123, 167, 215, 0.85);
  color: #1e4e79;
}
.tenant-chip-selected,
.tenant-filter-chip.tenant-chip-selected {
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.2);
  padding: 1px 7px;
}
.tenant-type-cm {
  background-color: rgba(13, 110, 253, 0.85);
  border-color: rgba(13, 110, 253, 0.85);
  color: #ffffff;
}
.tenant-type-tbn {
  background-color: rgba(253, 126, 20, 0.85);
  border-color: rgba(253, 126, 20, 0.85);
  color: #ffffff;
}
.tenant-type-fasttrak {
  background-color: rgba(111, 66, 193, 0.85);
  border-color: rgba(111, 66, 193, 0.85);
  color: #ffffff;
}
.tenant-telematics-samsara {
  background-color: rgba(32, 201, 151, 0.85);
  border-color: rgba(32, 201, 151, 0.85);
  color: #ffffff;
}
.tenant-telematics-saucon {
  background-color: rgba(23, 162, 184, 0.85);
  border-color: rgba(23, 162, 184, 0.85);
  color: #ffffff;
}
.tenant-telematics-zonar {
  background-color: rgba(214, 51, 132, 0.85);
  border-color: rgba(214, 51, 132, 0.85);
  color: #ffffff;
}
.tenant-telematics-motive {
  background-color: rgba(255, 193, 7, 0.85);
  border-color: rgba(224, 168, 0, 0.85);
  color: #212529;
}
.tenant-telematics-none {
  background-color: rgba(108, 117, 125, 0.85);
  border-color: rgba(108, 117, 125, 0.85);
  color: #ffffff;
}
.tenant-maint-fleetio {
  background-color: rgba(121, 85, 72, 0.85);
  border-color: rgba(121, 85, 72, 0.85);
  color: #ffffff;
}
.tenant-maint-rta {
  background-color: rgba(142, 68, 173, 0.85);
  border-color: rgba(142, 68, 173, 0.85);
  color: #ffffff;
}
.tenant-maint-tbn {
  background-color: rgba(232, 89, 12, 0.85);
  border-color: rgba(217, 72, 15, 0.85);
  color: #ffffff;
}
.tenant-org-img {
  background-color: rgba(102, 16, 242, 0.85);
  border-color: rgba(102, 16, 242, 0.85);
  color: #ffffff;
}
.tenant-org-uma {
  background-color: rgba(25, 135, 84, 0.85);
  border-color: rgba(25, 135, 84, 0.85);
  color: #ffffff;
}
.tenant-org-aba {
  background-color: rgba(13, 202, 240, 0.85);
  border-color: rgba(10, 162, 192, 0.85);
  color: #055160;
}
.tenant-org-cba {
  background-color: rgba(253, 126, 20, 0.85);
  border-color: rgba(253, 126, 20, 0.85);
  color: #ffffff;
}
.tenant-org-gnema {
  background-color: rgba(108, 117, 125, 0.85);
  border-color: rgba(108, 117, 125, 0.85);
  color: #ffffff;
}
.tenant-chip-vehicle-tier {
  font-weight: bold;
}
.tenant-tier-1-9 {
  background-color: rgba(207, 226, 255, 0.85);
  border-color: rgba(158, 197, 254, 0.85);
  color: #084298;
}
.tenant-tier-10-49 {
  background-color: rgba(209, 231, 221, 0.85);
  border-color: rgba(163, 207, 187, 0.85);
  color: #0f5132;
}
.tenant-tier-50-99 {
  background-color: rgba(255, 243, 205, 0.85);
  border-color: rgba(255, 218, 106, 0.85);
  color: #664d03;
}
.tenant-tier-100-plus {
  background-color: rgba(248, 215, 218, 0.85);
  border-color: rgba(241, 174, 181, 0.85);
  color: #842029;
}
.tenant-tier-250-plus {
  background-color: rgba(226, 217, 243, 0.85);
  border-color: rgba(197, 179, 230, 0.85);
  color: #432874;
}
.tenant-filter-clickable {
  cursor: pointer;
}
.tenant-filter-clickable:hover {
  filter: brightness(0.95);
}
.tenant-features-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}
.tenant-manager-table >>> table {
  table-layout: auto;
  width: 100%;
}
.tenant-manager-table >>> tbody tr:nth-of-type(even) td {
  background-color: #ececec !important;
}
.tenant-manager-table >>> tbody tr:nth-of-type(odd) td {
  background-color: #ffffff !important;
}
.tenant-manager-table >>> tbody tr:nth-of-type(even):hover td {
  background-color: #ececec !important;
}
.tenant-manager-table >>> tbody tr:nth-of-type(odd):hover td {
  background-color: #ffffff !important;
}
.tenant-filter-sep {
  color: #999;
  padding: 0 1px;
  user-select: none;
}
.tenant-search-row {
  gap: 6px;
}
.tenant-search-input {
  flex: 0 0 180px;
  width: 180px;
  max-width: 180px;
}
.tenant-search-btn {
  flex: 0 0 auto;
}
.tenant-setting-key-select {
  flex: 0 1 280px;
  width: 280px;
  max-width: 100%;
}
.tenant-setting-value-select {
  flex: 0 0 110px;
  width: 110px;
}
.tenant-data-td {
  max-width: none !important;
  white-space: nowrap;
}
.tenant-pricing-td {
  font-weight: bold;
  text-align: right;
  vertical-align: top;
  white-space: normal !important;
}
.tenant-pricing-exclude-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid #dee2e6;
  width: 100%;
  white-space: nowrap;
}
.tenant-pricing-exclude-row >>> .custom-control-label {
  font-size: 0.8rem;
  padding-left: 0.15rem;
}
.tenant-manager-table >>> tr.tenant-pricing-excluded-row td.tenant-pricing-td {
  opacity: 0.45;
}
.tenant-pricing-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  line-height: 1.25;
  width: 100%;
}
.tenant-pricing-override-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
}
.tenant-pricing-computed {
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.tenant-pricing-override-input {
  flex: 0 0 56px;
  width: 56px;
  min-width: 56px;
  padding: 1px 4px;
  font-size: 11px;
  font-weight: normal;
  text-align: right;
}
.tenant-pricing-override-input.tenant-pricing-override-active {
  border-color: var(--fi-action-primary);
  background-color: #f0f8ff;
}
.tenant-pricing-override-total {
  flex: 0 0 56px;
  width: 56px;
  min-width: 56px;
  font-size: 11px;
  font-weight: normal;
  text-align: right;
}
.tenant-pricing-override-total.tenant-pricing-override-active {
  color: var(--fi-action-primary);
  font-weight: 600;
}
.tenant-pricing-total-line {
  font-weight: bold;
  white-space: nowrap;
}
.tenant-pricing-package-line {
  font-size: 11px;
  font-weight: normal;
  color: #555;
  white-space: nowrap;
}
.tenant-stripe-unmapped {
  color: #c82333 !important;
  font-weight: bold;
}
.tenant-alerts-sms-over-estimate,
.tenant-alerts-sms-over-estimate .tenant-pricing-computed {
  color: #c82333 !important;
  font-weight: bold;
}
.tenant-stripe-subscription-link {
  color: inherit;
  text-decoration: underline;
  font-weight: bold;
}
.tenant-stripe-subscription-link:hover {
  color: #0056b3;
}
.tenant-stripe-customer-name {
  font-size: 11px;
  font-weight: bold;
  color: #333;
  white-space: normal;
  line-height: 1.2;
  margin-bottom: 2px;
}
.tenant-pricing-diff-package {
  font-weight: bold;
}
.tenant-group-toggle-btn {
  min-width: 28px;
  font-weight: bold;
  background-color: #495057 !important;
  border-color: #343a40 !important;
  color: #fff !important;
}
.tenant-group-toggle-btn:hover,
.tenant-group-toggle-btn:focus {
  background-color: #343a40 !important;
  border-color: #23272b !important;
  color: #fff !important;
}
.tenant-row-details-btn {
  min-width: 28px;
  font-weight: bold;
}
.tenant-row-details-card {
  text-align: left;
  border: none;
  background-color: #ffffff;
}
.tenant-row-details-empty {
  color: #666;
  font-style: italic;
  margin-bottom: 8px;
}
.tenant-row-details-actions {
  margin-top: 10px;
}
.tenant-row-details-capabilities {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
.tenant-row-details-caps-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: var(--fi-text-muted, #6b7280);
}
.tenant-row-details-json {
  margin-top: 10px;
  padding: 10px;
  background-color: #f8f9fa;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 480px;
  overflow: auto;
}
.tenant-manager-table >>> tr.b-table-details > td {
  max-width: none !important;
  white-space: normal !important;
  overflow: visible !important;
}
.tenant-pricing-total-chip {
  background-color: #fff3cd;
  border-color: #e0a800;
}
.tenant-pricing-cell {
  cursor: help;
  border-bottom: 1px dotted #666;
}
.tenant-pricing-diff-low {
  color: #856404;
}
.tenant-pricing-diff-medium {
  color: #c65300;
}
.tenant-pricing-diff-high {
  color: #c82333;
  font-weight: bold;
}
.tenant-billing-group-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border: 1px solid #6c757d;
  border-radius: 3px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  color: #495057;
  background-color: #f8f9fa;
}
.tenant-billing-group-marker {
  color: #6c757d;
  margin-right: 4px;
}
.tenant-billing-group-child-name {
  padding-left: 12px;
}
.tenant-billing-group-summary-name {
  font-weight: bold;
  white-space: nowrap;
}
.tenant-manager-table >>> tr.tenant-billing-group-summary td {
  border-top: 2px solid #868e96 !important;
  background-color: #e9ecef !important;
  font-weight: bold;
}
.tenant-manager-table >>> tr.tenant-billing-group-summary:hover td {
  background-color: #e9ecef !important;
}
.tenant-manager-table >>> tr.tenant-billing-group-member td {
  background-color: #f8f9fa !important;
}
.tenant-manager-table >>> tr.tenant-billing-group-member:nth-of-type(even) td {
  background-color: #f1f3f5 !important;
}
.tenant-manager-table >>> tr.tenant-billing-group-member:hover td {
  background-color: #f1f3f5 !important;
}
.tenant-name-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.25;
}
.tenant-name-line {
  font-weight: bold;
  white-space: nowrap;
}
.tenant-name-link {
  color: var(--fi-rail-accent);
  text-decoration: none;
}
.tenant-name-link:hover {
  text-decoration: underline;
}
.tenant-id-line {
  font-size: 11px;
  color: #555;
  word-break: break-all;
}
.tenant-meta-line {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.tenant-platforms-cell,
.tenant-orgs-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  white-space: normal !important;
}
.tenant-orgs-td {
  vertical-align: top;
}
.tenant-last-login-td {
  white-space: normal !important;
  vertical-align: top;
}
.tenant-last-login-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.25;
}
.tenant-last-login-time {
  font-size: 12px;
  white-space: nowrap;
}
.tenant-last-login-user {
  font-size: 11px;
  color: #555;
  word-break: break-all;
}
.tenant-name-th {
  width: 240px;
  min-width: 200px;
}
.tenant-name-td {
  min-width: 200px !important;
  max-width: none !important;
  white-space: normal !important;
  vertical-align: top;
}
.tenant-features-th {
  width: 220px;
  min-width: 180px;
}
.tenant-features-td {
  min-width: 180px !important;
  max-width: none !important;
  white-space: normal !important;
  vertical-align: top;
}
.tenant-features-cell {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 3px 4px;
  width: 100%;
  min-width: 0;
  max-width: none;
}
.tenant-users-table {
  width: 100%;
  max-width: 100%;
  font-size: 14px;
  line-height: 1.3;
  background-color: #ffffff;
  border-collapse: collapse;
}
.tenant-users-table th {
  font-weight: bold;
  font-size: 15px;
  text-align: center;
  padding: 0 6px 2px 6px;
  white-space: nowrap;
  background-color: #ffffff !important;
}
.tenant-users-table td {
  padding: 1px 6px 1px 0;
  vertical-align: top;
  max-width: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tenant-users-table tbody tr:nth-of-type(odd) td {
  background-color: #ffffff !important;
}
.tenant-users-table tbody tr:nth-of-type(even) td {
  background-color: rgba(144, 238, 144, 0.5) !important;
}
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:hover,
.tenant-users-table-no-hover tbody tr:hover {
  background-color: transparent !important;
  background-image: none !important;
  box-shadow: none !important;
}
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(odd):hover > td,
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(odd):hover td,
.tenant-users-table-no-hover tbody tr:nth-of-type(odd):hover td {
  background-color: #ffffff !important;
}
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(even):hover > td,
.tenant-manager-table >>> .tenant-users-table-no-hover tbody tr:nth-of-type(even):hover td,
.tenant-users-table-no-hover tbody tr:nth-of-type(even):hover td {
  background-color: rgba(144, 238, 144, 0.5) !important;
}
.tenant-manager-table >>> tbody tr:hover .tenant-users-table-no-hover tbody tr:nth-of-type(odd) td {
  background-color: #ffffff !important;
}
.tenant-manager-table >>> tbody tr:hover .tenant-users-table-no-hover tbody tr:nth-of-type(even) td {
  background-color: rgba(144, 238, 144, 0.5) !important;
}
.tenant-users-table .tenant-user-username {
  max-width: none;
  min-width: 160px;
  overflow: visible;
  text-overflow: clip;
}
.tenant-users-table .tenant-user-name {
  max-width: none;
  min-width: 120px;
}
.tenant-users-table .tenant-user-login {
  max-width: none;
  min-width: 140px;
}
.tenant-manager-table >>> td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 330px;
}
</style>

